import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "@/App.css";
import axios from "axios";

// Axios global interceptor - envoie le token d'auth avec chaque requête
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { 
  Users, Building2, Clock, CheckCircle2, Search, Phone, AlertTriangle,
  RefreshCw, Eye, Edit2, X, Home, Settings, Menu, Bell, ChevronRight, ChevronDown,
  FileText, Truck, DollarSign, HelpCircle, Shield, LogOut, Filter, TrendingUp,
  MessageSquare, History, Cog, Map, Receipt, UserCog, BarChart2, ArrowLeftRight,
  Calendar, Mail, ClipboardCheck, Compass, Lock, Briefcase, Crown, GraduationCap,
  CalendarPlus, MapPin, User, Trash2, Check, Brain, Sparkles, Send, Plus,
  Activity, PhoneCall, FileEdit, CalendarCheck, PlayCircle, Zap
} from "lucide-react";
import SearchPage from "./SearchPage";
import ChristelleSearch from "./ChristelleSearch";
import StatsPage from "./StatsPage";
import TeleproDashboard from "./TeleproDashboard";
import ClientCommunicationHistory from "./ClientCommunicationHistory";
import AdminPage from "./AdminPage";
import FacturesPage from "./FacturesPage";
import AdminUsersPage from "./AdminUsersPage";
import RapprochementPage from "./RapprochementPage";
import SuperBatardDashboard from "./SuperBatardDashboard";
import SuperBatardComptaDashboard from "./SuperBatardComptaDashboard";
import ConsolidationComptableV2 from "./components/ConsolidationComptableV2";
import ComptaFinancesDashboard from "./ComptaFinancesDashboard";
import ComptaFinancesDashboardNew from "./ComptaFinancesDashboardNew";
import MarketingDashboard from "./MarketingDashboard";
import CommunicationsHub from "./CommunicationsHub";
import ReunionRoom from "./ReunionRoom";
import AdministratifDashboard from "./AdministratifDashboard";
import ChantiersDashboard from "./ChantiersDashboard";
import GrandsComptesDashboard from "./GrandsComptesDashboard";
import CalendrierDashboard from "./CalendrierDashboard";
import MonEspace from "./MonEspace";
import ComiteDirectionDashboard from "./ComiteDirectionDashboard";
import FormationsDashboard from "./FormationsDashboard";
import SimulateurAidesDashboard from "./SimulateurAidesDashboard";
import AidesCompDashboard from "./components/AidesCompDashboard";
import TestsDashboard from "./TestsDashboard";
import { CalendrierButton } from './components/calendrier/CalendrierButton';
import { MonEspaceButton } from './components/MonEspaceButton';
import { ParcoursClientParchemin, AnalyseDossiersBloques, StatistiquesParcoursPage } from "./components/telepro";
import NeuralNetworkHero from "./components/NeuralNetworkHero";
import PipelineWidget from "./components/PipelineWidget";
import QualificationAppel from "./components/QualificationAppel";
import DevisCreationForm from "./components/DevisCreationForm";
import BanniereNouveauR1 from "./components/BanniereNouveauR1";
import BanniereLeadsLive from "./components/BanniereLeadsLive";
import TaskNotificationBadge from "./components/TaskNotificationBadge";
import TasksPanel from "./components/TasksPanel";
import LogementInfoSection from "./components/LogementInfoSection";
import MandataireANAHSection from "./components/MandataireANAHSection";
import EligibilitesTab from "./components/EligibilitesTab";
import WhatsAppExchangesTab from "./components/WhatsAppExchangesTab";
import DashboardNav from "./components/DashboardNav";

import getApiUrl from './utils/getApiUrl';

const API = `${getApiUrl()}/api`;

// Helper: identifiant client pour les API calls (préfère client_id, fallback row_number)
const cid = (client) => encodeURIComponent(client?.client_id || client?._clientId || client?.row_number || '');


const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// ═══════════════════════════════════════════════════════════════
// POLE ICONS & COLORS
// ═══════════════════════════════════════════════════════════════
const POLE_CONFIG = {
  'Télépro': { icon: Phone, color: 'blue', bg: 'bg-blue-500' },
  'R1': { icon: Home, color: 'green', bg: 'bg-green-500' },
  'Administratif': { icon: FileText, color: 'slate', bg: 'bg-slate-500' },
  'AMO': { icon: Building2, color: 'violet', bg: 'bg-violet-500' },
  'R2': { icon: FileText, color: 'cyan', bg: 'bg-cyan-500' },
  'Fin-RAC': { icon: DollarSign, color: 'emerald', bg: 'bg-emerald-500' },
  'Fin-Avances': { icon: DollarSign, color: 'teal', bg: 'bg-teal-500' },
  'Fin-Soldes': { icon: DollarSign, color: 'lime', bg: 'bg-lime-500' },
  'Chantiers': { icon: Truck, color: 'orange', bg: 'bg-orange-500' },
  'Aides Comp.': { icon: HelpCircle, color: 'pink', bg: 'bg-pink-500' },
  'TC/PAC ISO': { icon: Settings, color: 'indigo', bg: 'bg-indigo-500' },
  'Super Admin': { icon: Shield, color: 'red', bg: 'bg-red-500', displayName: 'Super Batard 😈' },
};

// ═══════════════════════════════════════════════════════════════
// POLE SELECTOR (LOGIN)
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// DASHBOARD CONFIGURATION FOR DYNAMIC ORDERING
// ═══════════════════════════════════════════════════════════════
const DASHBOARD_COMPONENTS = {
  salle_reunion: {
    id: 'salle_reunion',
    label: '🏛️ Salle de Réunion',
    description: 'Réunions virtuelles IA',
    icon: Calendar,
    gradient: 'from-purple-900/80 to-pink-900/80',
    borderColor: 'border-purple-500/50',
    hoverBorder: 'hover:border-purple-400',
    hoverShadow: 'hover:shadow-purple-500/20',
    iconGradient: 'from-purple-500 to-pink-500',
    textColor: 'text-purple-300',
    testId: 'salle-reunion-btn',
  },
  telepro: {
    id: 'telepro',
    label: '📞 Télépro',
    description: 'Prospection téléphonique',
    icon: Phone,
    gradient: 'from-blue-900/80 to-indigo-900/80',
    borderColor: 'border-blue-500/50',
    hoverBorder: 'hover:border-blue-400',
    hoverShadow: 'hover:shadow-blue-500/20',
    iconGradient: 'from-blue-500 to-indigo-500',
    textColor: 'text-blue-300',
    testId: 'telepro-btn',
    poleName: 'Télépro',
  },
  r1: {
    id: 'r1',
    label: '🔍 R1',
    description: 'Prévisite audit technique',
    icon: ClipboardCheck,
    gradient: 'from-green-900/80 to-emerald-900/80',
    borderColor: 'border-green-500/50',
    hoverBorder: 'hover:border-green-400',
    hoverShadow: 'hover:shadow-green-500/20',
    iconGradient: 'from-green-500 to-emerald-500',
    textColor: 'text-green-300',
    testId: 'r1-btn',
    poleName: 'R1',
  },
  administratif: {
    id: 'administratif',
    label: '📋 Administratif',
    description: 'Gestion des dossiers',
    icon: FileText,
    gradient: 'from-slate-800/80 to-gray-900/80',
    borderColor: 'border-slate-500/50',
    hoverBorder: 'hover:border-slate-400',
    hoverShadow: 'hover:shadow-slate-500/20',
    iconGradient: 'from-slate-500 to-gray-600',
    textColor: 'text-slate-300',
    testId: 'administratif-btn',
  },
  amo: {
    id: 'amo',
    label: '🏢 AMO',
    description: "Assistance maîtrise d'ouvrage",
    icon: Building2,
    gradient: 'from-violet-900/80 to-purple-900/80',
    borderColor: 'border-violet-500/50',
    hoverBorder: 'hover:border-violet-400',
    hoverShadow: 'hover:shadow-violet-500/20',
    iconGradient: 'from-violet-500 to-purple-500',
    textColor: 'text-violet-300',
    testId: 'amo-btn',
    poleName: 'AMO',
  },
  r2: {
    id: 'r2',
    label: '📐 R2',
    description: 'Sortie architecte',
    icon: Compass,
    gradient: 'from-cyan-900/80 to-teal-900/80',
    borderColor: 'border-cyan-500/50',
    hoverBorder: 'hover:border-cyan-400',
    hoverShadow: 'hover:shadow-cyan-500/20',
    iconGradient: 'from-cyan-500 to-teal-500',
    textColor: 'text-cyan-300',
    testId: 'r2-btn',
    poleName: 'R2',
  },
  chantiers: {
    id: 'chantiers',
    label: '🚚 Chantiers',
    description: 'Suivi des travaux',
    icon: Truck,
    gradient: 'from-orange-900/80 to-amber-900/80',
    borderColor: 'border-orange-500/50',
    hoverBorder: 'hover:border-orange-400',
    hoverShadow: 'hover:shadow-orange-500/20',
    iconGradient: 'from-orange-500 to-amber-500',
    textColor: 'text-orange-300',
    testId: 'chantiers-btn',
  },
  aides_comp: {
    id: 'aides_comp',
    label: '💝 Aides Comp.',
    description: 'Aides complémentaires',
    icon: HelpCircle,
    gradient: 'from-pink-900/80 to-rose-900/80',
    borderColor: 'border-pink-500/50',
    hoverBorder: 'hover:border-pink-400',
    hoverShadow: 'hover:shadow-pink-500/20',
    iconGradient: 'from-pink-500 to-rose-500',
    textColor: 'text-pink-300',
    testId: 'aides-comp-btn',
    poleName: 'Aides Comp.',
  },
  tc_pac_iso: {
    id: 'tc_pac_iso',
    label: '⚙️ TC/PAC ISO',
    description: 'Techniques & PAC',
    icon: Settings,
    gradient: 'from-indigo-900/80 to-blue-900/80',
    borderColor: 'border-indigo-500/50',
    hoverBorder: 'hover:border-indigo-400',
    hoverShadow: 'hover:shadow-indigo-500/20',
    iconGradient: 'from-indigo-500 to-blue-500',
    textColor: 'text-indigo-300',
    testId: 'tc-pac-btn',
    poleName: 'TC/PAC ISO',
  },
  communications_ia: {
    id: 'communications_ia',
    label: '📨 Communications IA',
    description: 'Traitement & Enrichissement',
    icon: Mail,
    gradient: 'from-sky-900/80 to-blue-900/80',
    borderColor: 'border-sky-500/50',
    hoverBorder: 'hover:border-sky-400',
    hoverShadow: 'hover:shadow-sky-500/20',
    iconGradient: 'from-sky-500 to-blue-600',
    textColor: 'text-sky-300',
    testId: 'communications-hub-btn',
  },
  marketing: {
    id: 'marketing',
    label: '📣 Marketing & Com',
    description: 'Acquisition & Communication',
    icon: MessageSquare,
    gradient: 'from-rose-900/80 to-pink-900/80',
    borderColor: 'border-rose-500/50',
    hoverBorder: 'hover:border-rose-400',
    hoverShadow: 'hover:shadow-rose-500/20',
    iconGradient: 'from-rose-500 to-pink-500',
    textColor: 'text-rose-300',
    testId: 'marketing-btn',
  },
  grands_comptes: {
    id: 'grands_comptes',
    label: '🏢 Grands Comptes',
    description: 'CCAS & Prescripteurs',
    icon: Briefcase,
    gradient: 'from-amber-900/80 to-yellow-900/80',
    borderColor: 'border-amber-500/50',
    hoverBorder: 'hover:border-amber-400',
    hoverShadow: 'hover:shadow-amber-500/20',
    iconGradient: 'from-amber-500 to-yellow-500',
    textColor: 'text-amber-300',
    testId: 'grands-comptes-btn',
  },
  calendrier: {
    id: 'calendrier',
    label: '📅 Calendrier',
    description: 'RDV R1 & R2',
    icon: Calendar,
    gradient: 'from-teal-900/80 to-cyan-900/80',
    borderColor: 'border-teal-500/50',
    hoverBorder: 'hover:border-teal-400',
    hoverShadow: 'hover:shadow-teal-500/20',
    iconGradient: 'from-teal-500 to-cyan-500',
    textColor: 'text-teal-300',
    testId: 'calendrier-btn',
  },
  comite_direction: {
    id: 'comite_direction',
    label: '👔 Comité de Direction',
    description: 'Échanges avec les directeurs IA',
    icon: Crown,
    gradient: 'from-amber-900/80 to-orange-900/80',
    borderColor: 'border-amber-500/50',
    hoverBorder: 'hover:border-amber-400',
    hoverShadow: 'hover:shadow-amber-500/20',
    iconGradient: 'from-amber-500 to-orange-500',
    textColor: 'text-amber-300',
    testId: 'comite-direction-btn',
  },
  formations: {
    id: 'formations',
    label: '🎓 Formations',
    description: 'Montée en compétences avec Fatima',
    icon: GraduationCap,
    gradient: 'from-purple-900/80 to-pink-900/80',
    borderColor: 'border-purple-500/50',
    hoverBorder: 'hover:border-purple-400',
    hoverShadow: 'hover:shadow-purple-500/20',
    iconGradient: 'from-purple-500 to-pink-500',
    textColor: 'text-purple-300',
    testId: 'formations-btn',
  },
  simulateur_aides: {
    id: 'simulateur_aides',
    label: '🏠 Simulateur Aides',
    description: 'Calcul aides rénovation énergétique',
    icon: Home,
    gradient: 'from-lime-900/80 to-emerald-900/80',
    borderColor: 'border-lime-500/50',
    hoverBorder: 'hover:border-lime-400',
    hoverShadow: 'hover:shadow-lime-500/20',
    iconGradient: 'from-lime-500 to-emerald-500',
    textColor: 'text-lime-300',
    testId: 'simulateur-aides-btn',
  },
  super_batard: {
    id: 'super_batard',
    label: '😈 Super Batard',
    description: 'Admin & Configuration IA',
    icon: Shield,
    gradient: 'from-red-900/80 to-orange-900/80',
    borderColor: 'border-red-500/50',
    hoverBorder: 'hover:border-red-400',
    hoverShadow: 'hover:shadow-red-500/20',
    iconGradient: 'from-red-500 to-orange-500',
    textColor: 'text-red-300',
    testId: 'super-batard-btn',
  },
  tests: {
    id: 'tests',
    label: '🧪 Tests',
    description: 'Environnement de test modales',
    icon: Compass,
    gradient: 'from-purple-900/80 to-pink-900/80',
    borderColor: 'border-purple-500/50',
    hoverBorder: 'hover:border-purple-400',
    hoverShadow: 'hover:shadow-purple-500/20',
    iconGradient: 'from-purple-500 to-pink-500',
    textColor: 'text-purple-300',
    testId: 'tests-dashboard-btn',
    adminOnly: true,
  },
  // Dashboards COMPTA (à mettre en fin par défaut)
  compta_finances: {
    id: 'compta_finances',
    label: '💰 Compta / Finances',
    description: 'Rapprochements & Stats',
    icon: DollarSign,
    gradient: 'from-emerald-900/80 to-teal-900/80',
    borderColor: 'border-emerald-500/50',
    hoverBorder: 'hover:border-emerald-400',
    hoverShadow: 'hover:shadow-emerald-500/20',
    iconGradient: 'from-emerald-500 to-teal-500',
    textColor: 'text-emerald-300',
    testId: 'compta-finances-btn',
    category: 'compta',
  },
  compta_fi_new: {
    id: 'compta_fi_new',
    label: '💰 Compta / Finances',
    description: 'Validation Pennylane',
    icon: Send,
    gradient: 'from-orange-900/80 to-red-900/80',
    borderColor: 'border-orange-500/50',
    hoverBorder: 'hover:border-orange-400',
    hoverShadow: 'hover:shadow-orange-500/20',
    iconGradient: 'from-orange-500 to-red-500',
    textColor: 'text-orange-300',
    testId: 'compta-fi-new-btn',
    badge: 'NEW',
    badgeColor: 'bg-orange-500',
    category: 'compta',
  },
  factures_fournisseurs: {
    id: 'factures_fournisseurs',
    label: 'Factures Fournisseurs',
    description: 'Dépôt & suivi factures',
    icon: FileText,
    gradient: 'from-orange-900/80 to-amber-900/80',
    borderColor: 'border-orange-500/50',
    hoverBorder: 'hover:border-orange-400',
    hoverShadow: 'hover:shadow-orange-500/20',
    iconGradient: 'from-orange-500 to-amber-500',
    textColor: 'text-orange-300',
    testId: 'factures-fournisseurs-btn',
    category: 'compta',
  },
  consolidation_v2: {
    id: 'consolidation_v2',
    label: '🧪 Compta (Test)',
    description: 'Consolidation v2 multi-tenant',
    icon: BarChart2,
    gradient: 'from-purple-900/80 to-pink-900/80',
    borderColor: 'border-purple-500/50',
    hoverBorder: 'hover:border-purple-400',
    hoverShadow: 'hover:shadow-purple-500/20',
    iconGradient: 'from-purple-500 to-pink-500',
    textColor: 'text-purple-300',
    testId: 'consolidation-v2-btn',
    badge: 'TEST',
    badgeColor: 'bg-orange-500',
    category: 'compta',
  },
  super_batard_compta: {
    id: 'super_batard_compta',
    label: '⚙️ Admin Compta v2',
    description: 'Config Rachel multi-tenant',
    icon: Settings,
    gradient: 'from-red-900/80 to-orange-900/80',
    borderColor: 'border-red-500/50',
    hoverBorder: 'hover:border-red-400',
    hoverShadow: 'hover:shadow-red-500/20',
    iconGradient: 'from-red-500 to-orange-500',
    textColor: 'text-red-300',
    testId: 'super-batard-compta-btn',
    badge: 'ADMIN',
    badgeColor: 'bg-red-500',
    category: 'compta',
  },
};

// Default dashboard order
const DEFAULT_DASHBOARD_ORDER = [
  'salle_reunion', 'telepro', 'r1', 'administratif', 'amo', 'r2', 'chantiers',
  'aides_comp', 'tc_pac_iso', 'communications_ia', 'marketing', 'grands_comptes',
  'calendrier', 'comite_direction', 'formations', 'simulateur_aides', 'super_batard', 'tests',
  // Compta at the end
  'compta_fi_new', 'factures_fournisseurs', 'consolidation_v2', 'super_batard_compta'
];

const PoleSelector = ({ poles, onSelect, onSearchClick, onChristelleClick, onStatsClick, onAdminClick, onParcoursClick, onFacturesClick, onAnalyseBloques, onStatistiquesParcours, onRapprochementClick, onComptaFinancesClick, onComptaFiNewClick, onSalleReunionClick, onMarketingClick, onSuperBatardClick, onSuperBatardComptaClick, onConsolidationV2Click, onCommunicationsHubClick, onAdministratifClick, onChantiersClick, onAidesCompClick, onGrandsComptesClick, onCalendrierClick, onComiteDirectionClick, onFormationsClick, onMonEspaceClick, onSimulateurAidesClick, onTestsDashboardClick, onQualificationAppelClick, onConsoleAdminClick, authUser, onLogout, onUsersClick }) => {
  
  // Helper function to find pole by name
  const findPole = (name) => poles.find(p => p.name === name);
  
  // State pour le modal d'accès refusé
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const [deniedDashboard, setDeniedDashboard] = useState("");
  
  // State pour l'ordre dynamique des dashboards
  const [dashboardOrder, setDashboardOrder] = useState(DEFAULT_DASHBOARD_ORDER);
  const [dashboardVisibility, setDashboardVisibility] = useState({});
  
  // Visibilite des dashboards au niveau tenant (visible / hidden / coming_soon)
  const [tenantDashVisibility, setTenantDashVisibility] = useState({});
  
  // Charger l'ordre des dashboards depuis l'API
  useEffect(() => {
    const loadDashboardOrder = async () => {
      try {
        const res = await authFetch(`${API}/config/dashboards-order`);
        if (res.ok) {
          const data = await res.json();
          if (data.dashboards && data.dashboards.length > 0) {
            // Extraire l'ordre et la visibilité
            const order = data.dashboards.map(d => d.id);
            const visibility = {};
            data.dashboards.forEach(d => {
              visibility[d.id] = d.visible !== false;
            });
            // Ajouter les dashboards manquants à la fin
            const existingIds = new Set(order);
            DEFAULT_DASHBOARD_ORDER.forEach(id => {
              if (!existingIds.has(id)) {
                order.push(id);
                visibility[id] = true;
              }
            });
            setDashboardOrder(order);
            setDashboardVisibility(visibility);
          }
        }
      } catch (e) {
        console.error('Erreur chargement ordre dashboards:', e);
      }
    };
    loadDashboardOrder();
  }, []);

  // Charger la visibilite des dashboards au niveau tenant
  useEffect(() => {
    const loadTenantDashVisibility = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        if (!token) return;
        const res = await authFetch(`${API}/admin/tenant-permissions/my-dashboard-visibility`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (!data.is_platform_admin && data.visibility) {
            setTenantDashVisibility(data.visibility);
          }
        }
      } catch (e) {
        console.error("Erreur chargement visibilite tenant:", e);
      }
    };
    loadTenantDashVisibility();
  }, []);
  
  // Map des handlers pour chaque dashboard
  const dashboardHandlers = {
    salle_reunion: onSalleReunionClick,
    telepro: () => { const p = findPole('Télépro'); if(p) onSelect(p); },
    r1: () => { const p = findPole('R1'); if(p) onSelect(p); },
    administratif: onAdministratifClick,
    amo: () => { const p = findPole('AMO'); if(p) onSelect(p); },
    r2: () => { const p = findPole('R2'); if(p) onSelect(p); },
    chantiers: onChantiersClick,
    aides_comp: onAidesCompClick,
    tc_pac_iso: () => { const p = findPole('TC/PAC ISO'); if(p) onSelect(p); },
    communications_ia: onCommunicationsHubClick,
    marketing: onMarketingClick,
    grands_comptes: onGrandsComptesClick,
    calendrier: onCalendrierClick,
    comite_direction: onComiteDirectionClick,
    formations: onFormationsClick,
    simulateur_aides: onSimulateurAidesClick,
    super_batard: onSuperBatardClick,
    console_admin: onConsoleAdminClick,
    tests: onTestsDashboardClick,
    compta_finances: onComptaFinancesClick,
    compta_fi_new: onComptaFiNewClick,
    factures_fournisseurs: onFacturesClick,
    consolidation_v2: onConsolidationV2Click,
    super_batard_compta: onSuperBatardComptaClick,
  };
  
  // Récupérer les permissions dashboard de l'utilisateur
  const dashboardAccess = authUser?.dashboard_access || {};
  
  // Detecter si l'utilisateur est admin de la plateforme (pas juste admin d'un tenant)
  const isPlatformAdmin = authUser?.email === "sdi@sani-adapt.fr" || authUser?.tenant_id === "tenant_default";
  
  // Fonction pour vérifier si l'utilisateur a accès à un dashboard
  // Les admins ont accès à tout
  const hasAccess = (dashboardKey) => {
    // Super Batard est UNIQUEMENT pour l'admin plateforme
    if (dashboardKey === 'super_batard' || dashboardKey === 'super_batard_compta') {
      return isPlatformAdmin;
    }
    
    // Console Admin est pour les admins tenant uniquement
    if (dashboardKey === 'console_admin') {
      return !isPlatformAdmin && authUser?.role === 'admin';
    }
    
    // Les admins plateforme ont toujours accès
    if (isPlatformAdmin) return true;
    
    // Les admins tenant ont accès sauf aux dashboards restreints plateforme
    if (authUser?.role === 'admin') {
      const platformOnlyDashboards = ['super_batard', 'super_batard_compta'];
      if (platformOnlyDashboards.includes(dashboardKey)) return false;
      return true;
    }
    
    // Si pas de permissions définies du tout, utiliser les valeurs par défaut
    if (!authUser?.dashboard_access || Object.keys(authUser.dashboard_access).length === 0) {
      // Par défaut, certains dashboards sont restreints
      const restrictedByDefault = ['compta_finances', 'factures_fournisseurs', 'communications_ia', 'marketing', 'grands_comptes', 'comite_direction', 'super_batard'];
      return !restrictedByDefault.includes(dashboardKey);
    }
    
    // Si la clé existe, utiliser sa valeur
    if (dashboardKey in dashboardAccess) {
      return dashboardAccess[dashboardKey] === true;
    }
    
    // Si la clé n'existe pas (ancien format de permissions), refuser par défaut pour les dashboards sensibles
    const restrictedByDefault = ['compta_finances', 'factures_fournisseurs', 'communications_ia', 'marketing', 'grands_comptes', 'comite_direction', 'super_batard'];
    return !restrictedByDefault.includes(dashboardKey);
  };
  
  // Visibilite au niveau tenant : "visible" (defaut), "hidden", "coming_soon"
  const getTenantDashState = (dashboardKey) => {
    if (isPlatformAdmin) return "visible";
    if (!tenantDashVisibility || Object.keys(tenantDashVisibility).length === 0) return "visible";
    return tenantDashVisibility[dashboardKey] || "visible";
  };

  // Handler qui vérifie les permissions avant d'exécuter l'action
  const handleDashboardClick = (dashboardKey, dashboardLabel, action) => {
    const tenantState = getTenantDashState(dashboardKey);
    if (tenantState === "coming_soon") {
      setDeniedDashboard(dashboardLabel + " (Bientôt disponible)");
      setShowAccessDenied(true);
      return;
    }
    if (hasAccess(dashboardKey)) {
      action();
    } else {
      setDeniedDashboard(dashboardLabel);
      setShowAccessDenied(true);
    }
  };
  
  return (
    <div className="min-h-screen bg-[#0B1120] overflow-y-auto p-4 pb-20">
      {/* Modal d'accès refusé */}
      {showAccessDenied && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setShowAccessDenied(false)}>
          <div className="bg-slate-800 border border-slate-600 rounded-2xl p-8 max-w-md mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                <Lock className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Accès refusé</h3>
              <p className="text-slate-400 mb-6">
                Vous n'avez pas les accréditations pour accéder à <span className="text-white font-medium">{deniedDashboard}</span>.
              </p>
              <p className="text-sm text-slate-500 mb-6">
                Contactez votre administrateur pour demander l'accès.
              </p>
              <button
                onClick={() => setShowAccessDenied(false)}
                className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                Compris
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto py-4">
        {/* ═══ HEADER (user bar) ═══ */}
        <div className="flex items-center justify-end mb-2">
          {authUser && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-3 bg-slate-800/40 backdrop-blur-md border border-white/5 rounded-xl px-4 py-2">
                {authUser.picture ? (
                  <img src={authUser.picture} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 bg-[#007AFF] rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">{authUser.name?.[0] || authUser.email?.[0]}</span>
                  </div>
                )}
                <div className="hidden md:block">
                  <p className="text-white text-sm font-medium leading-tight">{authUser.name}</p>
                  <p className="text-slate-500 text-xs">{authUser.email}</p>
                </div>
                {authUser.role === 'admin' && (
                  <span className="px-2 py-0.5 bg-[#FFAD00]/20 text-[#FFAD00] text-xs rounded-full font-semibold">Admin</span>
                )}
              </div>
              
              {authUser.role === 'admin' && (
                <button
                  onClick={onUsersClick}
                  className="p-2.5 bg-slate-800/40 backdrop-blur-md border border-white/5 hover:border-[#007AFF]/30 rounded-xl transition-all text-slate-400 hover:text-white"
                  title="Gérer les accès"
                >
                  <UserCog className="w-4 h-4" />
                </button>
              )}
              <TaskNotificationBadge userEmail={authUser?.email} onOpenTasks={() => { if (typeof onMonEspaceClick === 'function') onMonEspaceClick('taches_internes'); }} />
              <button
                onClick={() => onMonEspaceClick()}
                className="p-2.5 bg-slate-800/40 backdrop-blur-md border border-white/5 hover:border-[#007AFF]/30 rounded-xl transition-all text-slate-400 hover:text-[#007AFF]"
                data-testid="mon-espace-home-btn"
                title="Mon Espace"
              >
                <User className="w-4 h-4" />
              </button>
              <button
                onClick={onLogout}
                className="p-2.5 bg-slate-800/40 backdrop-blur-md border border-white/5 hover:border-red-500/30 rounded-xl transition-all text-slate-400 hover:text-red-400"
                title="Déconnexion"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* ═══ HERO LOGO + NEURAL NETWORK ═══ */}
        <NeuralNetworkHero />

        {/* ═══ COMMAND CENTER ═══ */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          <button
            data-testid="search-multicriteria-btn"
            onClick={() => handleDashboardClick('recherche', 'Recherche multi-critères', onSearchClick)}
            className={`relative overflow-hidden rounded-xl bg-slate-800/40 backdrop-blur-md border border-white/5 p-6 flex items-center gap-4 transition-all hover:border-[#007AFF]/40 hover:shadow-lg hover:shadow-blue-500/10 group text-left ${!hasAccess('recherche') ? 'opacity-60' : ''}`}
          >
            {!hasAccess('recherche') && <Lock className="w-4 h-4 text-slate-600 absolute top-3 right-3" />}
            <div className="w-12 h-12 bg-[#007AFF]/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <Search className="w-6 h-6 text-[#007AFF]" />
            </div>
            <div>
              <h3 className="text-white font-bold text-lg">Recherche multi-critères</h3>
              <p className="text-slate-500 text-sm">Trouvez n'importe quel client instantanément</p>
            </div>
          </button>
          
          <button
            data-testid="christelle-search-btn"
            onClick={() => onChristelleClick && onChristelleClick()}
            className="relative overflow-hidden rounded-xl bg-[#FFAD00]/5 backdrop-blur-md border border-[#FFAD00]/20 p-6 flex items-center gap-4 transition-all hover:border-[#FFAD00]/50 hover:bg-[#FFAD00]/10 hover:shadow-lg hover:shadow-amber-500/10 group text-left"
          >
            <div className="w-12 h-12 bg-[#FFAD00]/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <Brain className="w-6 h-6 text-[#FFAD00]" />
            </div>
            <div>
              <h3 className="text-white font-bold text-lg">Christelle IA</h3>
              <p className="text-[#FFAD00]/60 text-sm">Assistant intelligent boosté par l'IA</p>
            </div>
            <Sparkles className="w-5 h-5 text-[#FFAD00]/50 ml-auto" />
          </button>
        </div>

        {/* ═══ BENTO GRID — Command Deck ═══ */}
        <div className="space-y-5">

          {/* ── ROW 1: Commercial / Vente (full width) ── */}
          <div data-testid="pole-commercial" className="rounded-2xl border border-blue-500/20 bg-gradient-to-br from-blue-950/30 to-slate-900/50 backdrop-blur-xl p-5 hover:border-blue-500/40 transition-colors duration-300">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Phone className="w-4 h-4 text-blue-400" />
              </div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-blue-400">Commercial / Vente</h3>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2.5">
              {[
                { id: 'telepro', icon: Phone, name: 'Télépro', desc: 'Prospection téléphonique', color: '#3B82F6' },
                { id: 'r1', icon: ClipboardCheck, name: 'R1', desc: 'Prévisite audit', color: '#34C759' },
                { id: 'grands_comptes', icon: Briefcase, name: 'Grands Comptes', desc: 'CCAS & Prescripteurs', color: '#F59E0B' },
                { id: 'tc_pac_iso', icon: Settings, name: 'TC/PAC ISO', desc: 'Techniques & PAC', color: '#6366F1' },
                { id: 'calendrier', icon: Calendar, name: 'Calendrier', desc: 'RDV R1 & R2', color: '#14B8A6' },
              ].filter(({ id }) => getTenantDashState(id) !== 'hidden').map(({ id, icon: Icon, name, desc, color }) => {
                const isComingSoon = getTenantDashState(id) === 'coming_soon';
                return (
                <button key={id} data-testid={`${id.replace(/_/g, '-')}-btn`} onClick={() => handleDashboardClick(id, name, dashboardHandlers[id])}
                  className={`flex flex-col items-start p-3.5 rounded-xl border border-white/5 bg-white/5 hover:bg-white/10 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] text-left ${isComingSoon ? 'opacity-40 cursor-default' : !hasAccess(id) ? 'opacity-50' : ''}`}>
                  {isComingSoon && <span className="absolute top-2 right-2 px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-500/30 text-amber-400">BIENTOT</span>}
                  {!isComingSoon && !hasAccess(id) && <Lock className="w-3 h-3 text-slate-600 absolute top-2 right-2" />}
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2.5" style={{ backgroundColor: `${color}20` }}>
                    <Icon className="w-4.5 h-4.5" style={{ color }} />
                  </div>
                  <h4 className="font-semibold text-white text-sm">{name}</h4>
                  <p className="text-[11px] text-white/40 mt-0.5">{desc}</p>
                </button>);
              })}
            </div>
          </div>

          {/* ── ROW 2: Chantiers + AMO + Finances + Marketing ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">

          {/* ── COL 1: Administratif + Chantiers (empilés) ── */}
          <div className="flex flex-col gap-5">

          {/* ── Administratif ── */}
          <div data-testid="pole-administratif" className="rounded-2xl border border-slate-500/20 bg-gradient-to-br from-slate-800/30 to-slate-900/50 backdrop-blur-xl p-5 hover:border-slate-500/40 transition-colors duration-300">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 rounded-lg bg-slate-500/20 flex items-center justify-center">
                <FileText className="w-4 h-4 text-slate-400" />
              </div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400">Administratif</h3>
            </div>
            <div className="grid grid-cols-1 gap-2.5">
              {[
                { id: 'administratif', icon: FileText, name: 'Administratif', desc: 'Gestion des dossiers', color: '#94A3B8' },
              ].filter(({ id }) => getTenantDashState(id) !== 'hidden').map(({ id, icon: Icon, name, desc, color }) => {
                const isComingSoon = getTenantDashState(id) === 'coming_soon';
                return (
                <button key={id} data-testid={`${id.replace(/_/g, '-')}-btn`} onClick={() => handleDashboardClick(id, name, dashboardHandlers[id])}
                  className={`flex flex-col items-start p-3.5 rounded-xl border border-white/5 bg-white/5 hover:bg-white/10 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] text-left ${isComingSoon ? 'opacity-40 cursor-default' : !hasAccess(id) ? 'opacity-50' : ''}`}>
                  {!isComingSoon && !hasAccess(id) && <Lock className="w-3 h-3 text-slate-600 absolute top-2 right-2" />}
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2.5" style={{ backgroundColor: `${color}20` }}>
                    <Icon className="w-4.5 h-4.5" style={{ color }} />
                  </div>
                  <h4 className="font-semibold text-white text-sm">{name}</h4>
                  <p className="text-[11px] text-white/40 mt-0.5">{desc}</p>
                </button>);
              })}
            </div>
          </div>

          {/* ── Chantiers ── */}
          <div data-testid="pole-chantiers" className="rounded-2xl border border-orange-500/20 bg-gradient-to-br from-orange-950/30 to-slate-900/50 backdrop-blur-xl p-5 hover:border-orange-500/40 transition-colors duration-300">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <Truck className="w-4 h-4 text-orange-400" />
              </div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-orange-400">Chantiers</h3>
            </div>
            <div className="grid grid-cols-1 gap-2.5">
              {[
                { id: 'r2', icon: Compass, name: 'R2', desc: 'Sortie architecte', color: '#0EA5E9' },
                { id: 'chantiers', icon: Truck, name: 'Chantiers', desc: 'Suivi des travaux', color: '#F97316' },
              ].filter(({ id }) => getTenantDashState(id) !== 'hidden').map(({ id, icon: Icon, name, desc, color }) => {
                const isComingSoon = getTenantDashState(id) === 'coming_soon';
                return (
                <button key={id} data-testid={`${id.replace(/_/g, '-')}-btn`} onClick={() => handleDashboardClick(id, name, dashboardHandlers[id])}
                  className={`flex flex-col items-start p-3.5 rounded-xl border border-white/5 bg-white/5 hover:bg-white/10 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] text-left ${isComingSoon ? 'opacity-40 cursor-default' : !hasAccess(id) ? 'opacity-50' : ''}`}>
                  {!isComingSoon && !hasAccess(id) && <Lock className="w-3 h-3 text-slate-600 absolute top-2 right-2" />}
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2.5" style={{ backgroundColor: `${color}20` }}>
                    <Icon className="w-4.5 h-4.5" style={{ color }} />
                  </div>
                  <h4 className="font-semibold text-white text-sm">{name}</h4>
                  <p className="text-[11px] text-white/40 mt-0.5">{desc}</p>
                </button>);
              })}
            </div>
          </div>

          </div>{/* fin COL 1: Administratif + Chantiers */}

          {/* ── AMO/MAR/CEE ── */}
          <div data-testid="pole-amo" className="rounded-2xl border border-purple-500/20 bg-gradient-to-br from-purple-950/30 to-slate-900/50 backdrop-blur-xl p-5 hover:border-purple-500/40 transition-colors duration-300">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Building2 className="w-4 h-4 text-purple-400" />
              </div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-purple-400">AMO/MAR/CEE</h3>
            </div>
            <div className="grid grid-cols-1 gap-2.5">
              {[
                { id: 'amo', icon: Building2, name: 'AMO/MAR', desc: "Maîtrise d'ouvrage & Mon Accompagnateur Rénov'", color: '#A855F7' },
                { id: 'aides_comp', icon: HelpCircle, name: 'Aides Comp.', desc: 'Aides complémentaires', color: '#EC4899' },
                { id: 'mandataires_cee', icon: Zap, name: 'Mandataires CEE', desc: 'Obligés pollueurs', color: '#22C55E' },
              ].filter(({ id }) => getTenantDashState(id) !== 'hidden').map(({ id, icon: Icon, name, desc, color }) => {
                const isComingSoon = id === 'mandataires_cee' || getTenantDashState(id) === 'coming_soon';
                return (
                <button key={id} data-testid={`${id.replace(/_/g, '-')}-btn`} onClick={() => !isComingSoon && handleDashboardClick(id, name, dashboardHandlers[id])}
                  className={`relative flex flex-col items-start p-3.5 rounded-xl border border-white/5 bg-white/5 hover:bg-white/10 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] text-left ${isComingSoon ? 'opacity-40 cursor-default' : !hasAccess(id) ? 'opacity-50' : ''}`}>
                  {isComingSoon && <span className="absolute top-2 right-2 px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-500/30 text-amber-400">BIENTOT</span>}
                  {!isComingSoon && !hasAccess(id) && <Lock className="w-3 h-3 text-slate-600 absolute top-2 right-2" />}
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2.5" style={{ backgroundColor: `${color}20` }}>
                    <Icon className="w-4.5 h-4.5" style={{ color }} />
                  </div>
                  <h4 className="font-semibold text-white text-sm">{name}</h4>
                  <p className="text-[11px] text-white/40 mt-0.5">{desc}</p>
                </button>);
              })}
            </div>
          </div>

          {/* ── Finances / Comptabilité ── */}
          <div data-testid="pole-finances" className="rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-950/30 to-slate-900/50 backdrop-blur-xl p-5 hover:border-emerald-500/40 transition-colors duration-300">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-emerald-400" />
              </div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-emerald-400">Finances / Compta</h3>
            </div>
            <div className="grid grid-cols-1 gap-2.5">
              {[
                { id: 'compta_fi_new', icon: Send, name: 'Compta / Finances', desc: 'Validation Pennylane', color: '#F97316' },
                { id: 'factures_fournisseurs', icon: FileText, name: 'Factures Fournisseurs', desc: 'Dépôt & suivi factures', color: '#F59E0B' },
              ].filter(({ id }) => getTenantDashState(id) !== 'hidden').map(({ id, icon: Icon, name, desc, color }) => {
                const isComingSoon = getTenantDashState(id) === 'coming_soon';
                return (
                <button key={id} data-testid={`${id.replace(/_/g, '-')}-btn`} onClick={() => handleDashboardClick(id, name, dashboardHandlers[id])}
                  className={`flex flex-col items-start p-3.5 rounded-xl border border-white/5 bg-white/5 hover:bg-white/10 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] text-left ${isComingSoon ? 'opacity-40 cursor-default' : !hasAccess(id) ? 'opacity-50' : ''}`}>
                  {!isComingSoon && !hasAccess(id) && <Lock className="w-3 h-3 text-slate-600 absolute top-2 right-2" />}
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2.5" style={{ backgroundColor: `${color}20` }}>
                    <Icon className="w-4.5 h-4.5" style={{ color }} />
                  </div>
                  <h4 className="font-semibold text-white text-sm">{name}</h4>
                  <p className="text-[11px] text-white/40 mt-0.5">{desc}</p>
                </button>);
              })}
            </div>
          </div>

          {/* ── Marketing / Communication ── */}
          <div data-testid="pole-marketing" className="rounded-2xl border border-pink-500/20 bg-gradient-to-br from-pink-950/30 to-slate-900/50 backdrop-blur-xl p-5 hover:border-pink-500/40 transition-colors duration-300">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 rounded-lg bg-pink-500/20 flex items-center justify-center">
                <MessageSquare className="w-4 h-4 text-pink-400" />
              </div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-pink-400">Marketing / Com</h3>
            </div>
            <div className="grid grid-cols-1 gap-2.5">
              {[
                { id: 'marketing', icon: MessageSquare, name: 'Marketing & Com', desc: 'Acquisition & Communication', color: '#EC4899' },
                { id: 'simulateur_aides', icon: Home, name: 'Simulateur Aides', desc: 'Calcul aides rénovation', color: '#22C55E' },
              ].filter(({ id }) => getTenantDashState(id) !== 'hidden').map(({ id, icon: Icon, name, desc, color }) => {
                const isComingSoon = getTenantDashState(id) === 'coming_soon';
                return (
                <button key={id} data-testid={`${id.replace(/_/g, '-')}-btn`} onClick={() => handleDashboardClick(id, name, dashboardHandlers[id])}
                  className={`flex flex-col items-start p-3.5 rounded-xl border border-white/5 bg-white/5 hover:bg-white/10 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] text-left ${isComingSoon ? 'opacity-40 cursor-default' : !hasAccess(id) ? 'opacity-50' : ''}`}>
                  {!isComingSoon && !hasAccess(id) && <Lock className="w-3 h-3 text-slate-600 absolute top-2 right-2" />}
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2.5" style={{ backgroundColor: `${color}20` }}>
                    <Icon className="w-4.5 h-4.5" style={{ color }} />
                  </div>
                  <h4 className="font-semibold text-white text-sm">{name}</h4>
                  <p className="text-[11px] text-white/40 mt-0.5">{desc}</p>
                </button>);
              })}
            </div>
          </div>

          </div>{/* fin ROW 2 */}

          {/* ── ROW 3: RH + Direction ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">

          {/* ── RH / Formation / Interne ── */}
          <div data-testid="pole-rh" className="lg:col-span-2 rounded-2xl border border-fuchsia-500/20 bg-gradient-to-br from-fuchsia-950/30 to-slate-900/50 backdrop-blur-xl p-5 hover:border-fuchsia-500/40 transition-colors duration-300">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 rounded-lg bg-fuchsia-500/20 flex items-center justify-center">
                <GraduationCap className="w-4 h-4 text-fuchsia-400" />
              </div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-fuchsia-400">RH / Formation</h3>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              {[
                { id: 'formations', icon: GraduationCap, name: 'Formations', desc: 'Compétences avec Fatima', color: '#D946EF' },
                { id: 'salle_reunion', icon: Calendar, name: 'Salle de Réunion', desc: 'Réunions virtuelles IA', color: '#8B5CF6' },
              ].filter(({ id }) => getTenantDashState(id) !== 'hidden').map(({ id, icon: Icon, name, desc, color }) => {
                const isComingSoon = getTenantDashState(id) === 'coming_soon';
                return (
                <button key={id} data-testid={`${id.replace(/_/g, '-')}-btn`} onClick={() => handleDashboardClick(id, name, dashboardHandlers[id])}
                  className={`flex flex-col items-start p-3.5 rounded-xl border border-white/5 bg-white/5 hover:bg-white/10 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] text-left ${isComingSoon ? 'opacity-40 cursor-default' : !hasAccess(id) ? 'opacity-50' : ''}`}>
                  {!isComingSoon && !hasAccess(id) && <Lock className="w-3 h-3 text-slate-600 absolute top-2 right-2" />}
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2.5" style={{ backgroundColor: `${color}20` }}>
                    <Icon className="w-4.5 h-4.5" style={{ color }} />
                  </div>
                  <h4 className="font-semibold text-white text-sm">{name}</h4>
                  <p className="text-[11px] text-white/40 mt-0.5">{desc}</p>
                </button>);
              })}
            </div>
          </div>

          {/* ── Direction / Pilotage ── */}
          <div data-testid="pole-direction" className="lg:col-span-2 rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-950/30 to-slate-900/50 backdrop-blur-xl p-5 hover:border-amber-500/40 transition-colors duration-300">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Crown className="w-4 h-4 text-amber-400" />
              </div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-amber-400">Direction / Pilotage</h3>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              {[
                { id: 'comite_direction', icon: Crown, name: 'Comité de Direction', desc: 'Échanges directeurs IA', color: '#F59E0B' },
                ...(isPlatformAdmin ? [{ id: 'super_batard', icon: Shield, name: 'Super Batard', desc: 'Admin & Configuration IA', color: '#EF4444' }] : []),
                ...(!isPlatformAdmin && authUser?.role === 'admin' ? [{ id: 'console_admin', icon: Shield, name: 'Console Admin', desc: 'Administration espace', color: '#3B82F6' }] : []),
              ].filter(({ id }) => getTenantDashState(id) !== 'hidden').map(({ id, icon: Icon, name, desc, color }) => {
                const isComingSoon = getTenantDashState(id) === 'coming_soon';
                return (
                <button key={id} data-testid={`${id.replace(/_/g, '-')}-btn`} onClick={() => handleDashboardClick(id, name, dashboardHandlers[id])}
                  className={`flex flex-col items-start p-3.5 rounded-xl border border-white/5 bg-white/5 hover:bg-white/10 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] text-left ${isComingSoon ? 'opacity-40 cursor-default' : !hasAccess(id) ? 'opacity-50' : ''}`}>
                  {!isComingSoon && !hasAccess(id) && <Lock className="w-3 h-3 text-slate-600 absolute top-2 right-2" />}
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2.5" style={{ backgroundColor: `${color}20` }}>
                    <Icon className="w-4.5 h-4.5" style={{ color }} />
                  </div>
                  <h4 className="font-semibold text-white text-sm">{name}</h4>
                  <p className="text-[11px] text-white/40 mt-0.5">{desc}</p>
                </button>);
              })}
            </div>
          </div>

          </div>{/* fin ROW 3 */}

          {/* ── Tests & Old ── */}
          {authUser?.role === 'admin' && (
          <div data-testid="pole-tests" className="rounded-2xl border border-slate-700/20 bg-slate-900/40 backdrop-blur-xl p-5 opacity-60 hover:opacity-100 transition-opacity duration-300">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 rounded-lg bg-slate-700/20 flex items-center justify-center">
                <Settings className="w-4 h-4 text-slate-500" />
              </div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">Tests & Old</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { id: 'tests', icon: Compass, name: 'Tests', desc: 'Environnement de test', color: '#8B5CF6', badge: 'TEST' },
                { id: 'communications_ia', icon: Mail, name: 'Communications IA', desc: 'Traitement & Enrichissement', color: '#0EA5E9', badge: 'OLD' },
                { id: 'compta_finances', icon: DollarSign, name: 'Compta old', desc: 'Rapprochements & Stats', color: '#6B7280', badge: 'OLD' },
                { id: 'consolidation_v2', icon: BarChart2, name: 'Compta (test)', desc: 'Consolidation v2', color: '#A855F7', badge: 'TEST' },
                { id: 'super_batard_compta', icon: Settings, name: 'Admin Compta v2', desc: 'Config Rachel', color: '#EF4444', badge: 'ADMIN' },
              ].filter(({ id }) => getTenantDashState(id) !== 'hidden').map(({ id, icon: Icon, name, desc, color, badge }) => {
                return (
                <button key={id} data-testid={`${id.replace(/_/g, '-')}-btn`} onClick={() => handleDashboardClick(id, name, dashboardHandlers[id])}
                  className="relative flex flex-col items-start p-3.5 rounded-xl border border-white/5 bg-white/5 hover:bg-white/10 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] text-left">
                  {badge && <span className="absolute top-2 right-2 px-1.5 py-0.5 text-[9px] font-bold rounded" style={{ backgroundColor: `${color}25`, color }}>{badge}</span>}
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2.5" style={{ backgroundColor: `${color}15` }}>
                    <Icon className="w-4.5 h-4.5" style={{ color }} />
                  </div>
                  <h4 className="font-semibold text-white/70 text-sm">{name}</h4>
                  <p className="text-[11px] text-white/30 mt-0.5">{desc}</p>
                </button>);
              })}
            </div>
          </div>
          )}

        </div>
        
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// ALERT CARD
// ═══════════════════════════════════════════════════════════════
const AlertCard = ({ alert, onClick, isActive }) => {
  const getCategoryColor = (category) => {
    const colors = {
      'TELEPRO': 'border-blue-500 bg-blue-500/10',
      'R1': 'border-green-500 bg-green-500/10',
      'VT AMO': 'border-purple-500 bg-purple-500/10',
      'R2': 'border-cyan-500 bg-cyan-500/10',
      'CHANTIER': 'border-orange-500 bg-orange-500/10',
      'DOSSIER': 'border-yellow-500 bg-yellow-500/10',
      'ADMIN': 'border-red-500 bg-red-500/10',
      'JUSTINE': 'border-pink-500 bg-pink-500/10',
      'RGT': 'border-indigo-500 bg-indigo-500/10',
      'AMO': 'border-purple-500 bg-purple-500/10',
    };
    return colors[category] || 'border-slate-500 bg-slate-500/10';
  };

  if (alert.count === 0) return null;

  return (
    <button
      data-testid={`alert-${alert.category}`}
      onClick={onClick}
      className={`w-full text-left p-4 rounded-lg border-l-4 transition-all ${getCategoryColor(alert.category)} ${isActive ? 'ring-2 ring-white' : 'hover:bg-opacity-20'}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-400 uppercase tracking-wider">{alert.category}</p>
          <p className="text-sm font-medium text-white truncate mt-1">{alert.status}</p>
          <p className="text-xs text-slate-500 mt-1">{alert.description}</p>
        </div>
        <div className="ml-4 flex-shrink-0">
          <span className="text-2xl font-bold text-white">{alert.count}</span>
        </div>
      </div>
    </button>
  );
};

// ═══════════════════════════════════════════════════════════════
// CLIENT DETAIL MODAL WITH TABS
// ═══════════════════════════════════════════════════════════════
const ClientModal = ({ client, columns, dropdowns, onClose, onUpdate, onPrendreRdv, onOpenCalendrier, authUser, onResumeQualification }) => {
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('infos');
  const [dynamicTabsConfig, setDynamicTabsConfig] = useState(null);
  const [devisSousTab, setDevisSousTab] = useState('mpa'); // 'mpa' | 'pac_iso' | 'tc'
  const [clientDevis, setClientDevis] = useState([]);
  const [loadingDevis, setLoadingDevis] = useState(false);
  const [factures, setFactures] = useState([]);
  const [facturesTotaux, setFacturesTotaux] = useState({ count: 0, total_ht: 0, total_ttc: 0 });
  const [loadingFactures, setLoadingFactures] = useState(false);
  
  // Encaissements client
  const [clientEncaissements, setClientEncaissements] = useState({ encaissements: [], total: 0, count: 0 });
  const [whatsappMsgCount, setWhatsappMsgCount] = useState(0);
  const [loadingEncaissements, setLoadingEncaissements] = useState(false);
  
  // Configuration des champs client et données Sheet
  const [clientFieldsConfig, setClientFieldsConfig] = useState(null);
  const [sheetData, setSheetData] = useState(null);
  const [loadingSheetData, setLoadingSheetData] = useState(false);
  
  // Champs personnalisés depuis MongoDB
  const [customFields, setCustomFields] = useState({});
  const [savingCustomField, setSavingCustomField] = useState(false);
  
  // Historique des échanges (emails rattachés)
  const [exchanges, setExchanges] = useState([]);
  const [loadingExchanges, setLoadingExchanges] = useState(false);
  const [selectedExchangeDetail, setSelectedExchangeDetail] = useState(null);
  
  // Historique des RDV
  const [rdvHistory, setRdvHistory] = useState([]);
  const [rdvSupprimes, setRdvSupprimes] = useState([]);
  const [loadingRdvHistory, setLoadingRdvHistory] = useState(false);
  
  // Rappels du client
  const [clientRappels, setClientRappels] = useState([]);
  const [loadingRappels, setLoadingRappels] = useState(false);
  
  // Pipeline status
  const [pipelineStage, setPipelineStage] = useState(null);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [loadingPipelineStage, setLoadingPipelineStage] = useState(false);
  const [changingStage, setChangingStage] = useState(false);
  const [showStageDropdown, setShowStageDropdown] = useState(false);
  
  // Timeline des activités
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  
  // État pour le formulaire Prendre RDV
  const [rdvFormData, setRdvFormData] = useState({
    nom: '',
    prenom: '',
    telephone: '',
    telephoneFixe: '',
    email: '',
    adresse: '',
    cp: '',
    ville: ''
  });
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [techniciensList, setTechniciensList] = useState([]);
  const [savingCoords, setSavingCoords] = useState(false);
  const [coordsSaved, setCoordsSaved] = useState(false);
  
  // État pour l'attribution du télépro
  const [teleprosList, setTeleprosList] = useState([]);
  const [telepro_assigne, setTelepro_assigne] = useState('');
  const [savingTeleproAssign, setSavingTeleproAssign] = useState(false);
  
  // État pour les personnes de confiance (max 2)
  const [personnesConfiance, setPersonnesConfiance] = useState([
    { nom: '', prenom: '', telPortable: '', telFixe: '', email: '', lienGroupe: '', lienType: '', notes: '', isPrincipal: true }
  ]);
  const [savingConfiance, setSavingConfiance] = useState(false);
  const [confianceSaved, setConfianceSaved] = useState(false);

  // Protection juridique (venant de la qualification)
  const [protectionJuridique, setProtectionJuridique] = useState('non');
  
  // Lire la protection juridique depuis sessionStorage ou la DB
  useEffect(() => {
    const qualData = window.sessionStorage.getItem('qualificationData');
    if (qualData) {
      try {
        const parsed = JSON.parse(qualData);
        if (parsed.protection_juridique) {
          setProtectionJuridique(parsed.protection_juridique);
        }
      } catch {}
    }
    // Ou depuis les données client en base
    if (client?.protection_juridique) {
      setProtectionJuridique(client.protection_juridique);
    }
  }, [client]);
  
  // Auto-scroll vers personne de confiance si tutelle/curatelle
  useEffect(() => {
    if (protectionJuridique === 'tutelle' || protectionJuridique === 'curatelle') {
      setTimeout(() => {
        const el = document.getElementById('section-personnes-confiance');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 500);
    }
  }, [protectionJuridique]);

  // Validation pour prise de RDV
  const canTakeRdv = () => {
    const tel = rdvFormData.telephone || sheetData?.tel_qhare || sheetData?.tel_client || '';
    const email = rdvFormData.email || sheetData?.email_qhare || '';
    const adresse = rdvFormData.adresse || '';
    const cp = rdvFormData.cp || '';
    const ville = rdvFormData.ville || '';
    const hasAdresse = adresse.trim() && cp.trim() && ville.trim();
    const hasContact = tel.trim() || email.trim();
    const onlyEmail = !tel.trim() && email.trim();
    return { canTake: hasAdresse && hasContact, hasAdresse, hasContact, onlyEmail };
  };

  // État pour le simulateur d'éligibilité MPA
  const [eligibilite, setEligibilite] = useState({
    zone: 'idf', // 'idf' ou 'province' - Par défaut Île-de-France
    nbPersonnes: 1,
    rfr: '',
    // Statut logement
    statutLogement: '', // 'proprietaire' ou 'locataire'
    typeResidence: '', // 'principale' ou 'secondaire' (si propriétaire)
    typeLocation: '', // 'prive' ou 'bailleur_social' (si locataire)
    rechercheBailleur: false,
    nomBailleur: '',
    reponseFatimaBailleur: null, // null, 'oui', 'non', 'loading'
    // Type de bien
    typeBien: '', // 'appartement' ou 'maison'
    // Âge et conditions médicales
    ageBeneficiaire: '',
    handicapMDPH: false, // Handicap MDPH ≥ 50%
    attestationGIR: false, // Attestation GIR
    engagementGIR: false, // S'engage à demander l'attestation
    // Déjà bénéficié MPA
    dejaBeneficieMPA: '', // 'oui' ou 'non'
    // Montant déjà utilisé (3 options)
    typeMontantConnu: '', // 'enveloppe', 'devis', 'approximatif', 'ne_sait_pas'
    montantEnveloppeAttribuee: '', // Montant de l'enveloppe ANAH déjà attribuée
    montantDevisHT: '', // Montant du devis HT signé
    montantApproximatif: '', // Montant approximatif
    travauxDejaRealises: [], // Types de travaux déjà faits
    // Produits sélectionnés (intérêt du client)
    produitsInteresses: [], // Produits qui intéressent le client (TOUJOURS demandé)
    // Travaux hors MPA (si non éligible)
    veutTravauxHorsMPA: false, // Le client veut faire des travaux hors subvention
    travauxHorsMPASelectionnes: [], // SDB hors prime, autres aides ANAH sélectionnées
    // Résultat
    resultat: null, // null, 'tres_modeste', 'modeste', 'non_eligible', 'conditionnel'
    motifNonEligible: '', // Raison si non éligible
    tauxPriseEnCharge: 0, // 70 ou 50
    enveloppeMax: 0, // 15400 (70% de 22k) ou 11000 (50% de 22k)
    montantUtilise: 0, // Montant déjà utilisé calculé
    montantRestant: 0, // Montant restant sur l'enveloppe
    checked: false
  });

  // Charger la simulation MPA sauvegardée pour les badges d'éligibilité
  // Fetch devis when tab is opened
  useEffect(() => {
    if (activeTab !== 'devis' || !client?.row_number) return;
    const loadDevis = async () => {
      setLoadingDevis(true);
      try {
        const r = await authFetch(`${API}/devis?client_row_number=${client.row_number}&client_id=${cid(client)}`);
        if (r.ok) setClientDevis(await r.json());
      } catch (e) { console.error(e); }
      setLoadingDevis(false);
    };
    loadDevis();
  }, [activeTab, client?.row_number]);


  useEffect(() => {
    const loadSavedSimulation = async () => {
      if (!client?.row_number) return;
      const clientId = client.data?.['ID QHARE'] || `row_${client.row_number}`;
      try {
        const response = await authFetch(`${API}/mpa/simulation/${clientId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.found && data.simulation) {
            const sim = data.simulation;
            setEligibilite({
              zone: sim.zone || 'idf',
              nbPersonnes: sim.nb_personnes || 1,
              rfr: sim.rfr?.toString() || '',
              statutLogement: sim.statut_logement || '',
              typeResidence: sim.type_residence || '',
              typeLocation: sim.type_location || '',
              rechercheBailleur: false,
              nomBailleur: '',
              reponseFatimaBailleur: null,
              typeBien: sim.type_bien || '',
              ageBeneficiaire: sim.age_beneficiaire?.toString() || '',
              handicapMDPH: sim.handicap_mdph || false,
              attestationGIR: sim.attestation_gir || false,
              engagementGIR: false,
              dejaBeneficieMPA: sim.deja_beneficie_mpa ? 'oui' : 'non',
              typeMontantConnu: sim.type_montant_connu || '',
              montantEnveloppeAttribuee: '',
              montantDevisHT: '',
              montantApproximatif: '',
              travauxDejaRealises: sim.travaux_deja_realises || [],
              produitsInteresses: sim.produits_interesses || [],
              veutTravauxHorsMPA: sim.veut_travaux_hors_mpa || false,
              travauxHorsMPASelectionnes: sim.travaux_hors_mpa_selectionnes || [],
              resultat: sim.resultat,
              motifNonEligible: '',
              tauxPriseEnCharge: sim.taux_prise_en_charge || 0,
              enveloppeMax: sim.enveloppe_max || 0,
              montantUtilise: sim.montant_utilise || 0,
              montantRestant: sim.montant_restant || 0,
              checked: true
            });
          }
        }
      } catch (error) {
        console.error('Erreur chargement simulation:', error);
      }
    };
    loadSavedSimulation();
  }, [client]);
  
  // Options pour le sélecteur de lien
  const liensOptions = {
    'Lien familial': [
      'Conjoint(e) / Époux(se)', 'Partenaire PACS', 'Concubin(e)', 
      'Fils', 'Fille', 'Gendre', 'Belle-fille', 
      'Petit-fils', 'Petite-fille', 'Frère', 'Sœur', 
      'Beau-frère', 'Belle-sœur', 'Neveu', 'Nièce', 'Cousin(e)',
      'Parent (Père / Mère)', 'Beau-parent', 'Grand-parent'
    ],
    'Entourage proche': [
      'Ami(e)', 'Voisin(e)', 'Aidant non familial', 'Personne de confiance désignée'
    ],
    'Aidant / Représentant légal': [
      'Aidant familial', 'Tuteur légal', 'Curateur', 
      'Mandataire judiciaire', 'Représentant légal', 'Personne habilitée (habilitation familiale)'
    ],
    'Professionnel': [
      'Assistant(e) social(e)', 'Infirmier(ère)', 'Aide à domicile',
      'Ergothérapeute', 'Gestionnaire de cas', 'Coordinateur(trice) médico-social',
      'Notaire', 'Avocat', 'Mandataire administratif'
    ],
    'Administratif / Organisme': [
      'CCAS', 'Conseil départemental', 'Caisse de retraite',
      'Mutuelle', 'Association d\'aide', 'Organisme d\'accompagnement'
    ]
  };
  
  // Charger les champs personnalisés depuis MongoDB
  const loadCustomFields = useCallback(async () => {
    if (!client?.row_number && !client?.client_id) return;
    
    try {
      const response = await authFetch(`${API}/client-custom-fields/${cid(client)}`);
      const data = await response.json();
      if (data.success && data.custom_fields) {
        setCustomFields(data.custom_fields);
      }
    } catch (error) {
      console.error('Erreur chargement champs custom:', error);
    }
  }, [client?.row_number, client?.client_id]);
  
  // Sauvegarder un champ personnalisé dans MongoDB
  const saveCustomField = async (fieldId, value) => {
    if (!client?.row_number && !client?.client_id) return false;
    
    setSavingCustomField(true);
    try {
      const response = await authFetch(`${API}/client-custom-fields/${cid(client)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ field_id: fieldId, value })
      });
      
      const data = await response.json();
      if (data.success) {
        setCustomFields(prev => ({ ...prev, [fieldId]: value }));
        console.log(`✅ Champ custom ${fieldId} sauvegardé dans MongoDB`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erreur sauvegarde champ custom:', error);
      return false;
    } finally {
      setSavingCustomField(false);
    }
  };
  
  // Obtenir la valeur d'un champ (Sheet + Custom MongoDB)
  const getFieldValue = useCallback((fieldId) => {
    // D'abord chercher dans les champs custom MongoDB
    if (customFields[fieldId] !== undefined) {
      return customFields[fieldId];
    }
    // Sinon chercher dans les données Sheet
    if (sheetData && sheetData[fieldId] !== undefined) {
      return sheetData[fieldId];
    }
    // Fallback vers client.data
    return client?.data?.[fieldId] || '';
  }, [customFields, sheetData, client?.data]);
  
  // Fonction pour sauvegarder les coordonnées
  const saveCoordonnees = async () => {
    if (!client?.row_number) return;
    
    setSavingCoords(true);
    setCoordsSaved(false);
    
    try {
      const resp = await authFetch(`${API}/pipeline/client/row_${cid(client)}/update-fields`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          telephone: rdvFormData.telephone,
          telephoneFixe: rdvFormData.telephoneFixe,
          email: rdvFormData.email,
          adresse: rdvFormData.adresse,
          code_postal: rdvFormData.cp,
          ville: rdvFormData.ville
        })
      });
      
      const data = await resp.json();
      
      if (resp.ok) {
        console.log("✅ Coordonnées sauvegardées:", data);
        setCoordsSaved(true);
        setTimeout(() => setCoordsSaved(false), 3000);
      } else {
        console.error("❌ Erreur sauvegarde:", data);
      }
    } catch (e) {
      console.error("❌ Erreur sauvegarde coordonnées:", e);
    }
    
    setSavingCoords(false);
  };
  
  // Sauvegarder les personnes de confiance
  const savePersonnesConfiance = async () => {
    if (!client?.row_number) return;
    
    setSavingConfiance(true);
    setConfianceSaved(false);
    
    try {
      const resp = await authFetch(`${API}/pipeline/client/row_${cid(client)}/personnes-confiance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ personnes: personnesConfiance })
      });
      
      const data = await resp.json();
      
      if (resp.ok) {
        console.log("✅ Personnes de confiance sauvegardées:", data);
        setConfianceSaved(true);
        setTimeout(() => setConfianceSaved(false), 3000);
      } else {
        console.error("❌ Erreur sauvegarde personnes confiance:", data);
      }
    } catch (e) {
      console.error("❌ Erreur sauvegarde personnes confiance:", e);
    }
    
    setSavingConfiance(false);
  };
  
  // Initialiser le formulaire RDV avec les données existantes
  useEffect(() => {
    if (client) {
      // D'abord initialiser avec les données du client standard
      setRdvFormData({
        nom: client.data?.['CLIENT NOM'] || '',
        prenom: client.data?.['PRÉNOM'] || '',
        telephone: client.data?.['TEL CLIENT'] || client.data?.['TEL'] || '',
        telephoneFixe: '',
        email: client.data?.['EMAIL'] || client.data?.['EMAIL CLIENT'] || '',
        adresse: client.data?.['ADRESSE'] || '',
        cp: client.data?.['CP'] || '',
        ville: client.data?.['VILLE'] || ''
      });
      
      // Charger la liste des techniciens
      authFetch(`${API}/planning/techniciens`)
        .then(res => res.json())
        .then(data => setTechniciensList(data.techniciens || []))
        .catch(e => console.error("Erreur chargement techniciens:", e));
      
      // Charger la liste des télépros pour l'attribution
      authFetch(`${API}/collaborateurs/all-assignables`)
        .then(res => res.json())
        .then(data => setTeleprosList(data.assignables || []))
        .catch(e => console.error("Erreur chargement télépros:", e));
      
      // Initialiser le télépro assigné depuis les données du client
      setTelepro_assigne(client.data?.['TÉLÉPRO ASSIGNÉ'] || client.data?.['TELEPRO'] || '');
      
      // Puis charger les données complètes depuis l'API pipeline
      const loadFullData = async () => {
        try {
          const resp = await authFetch(`${API}/pipeline/client/by-row/${cid(client)}`);
          if (resp.ok) {
            const data = await resp.json();
            console.log("📋 Données pipeline chargées:", data);
            setRdvFormData(prev => ({
              ...prev,
              nom: prev.nom && prev.nom !== '-' ? prev.nom : data.nom || prev.nom || '',
              prenom: prev.prenom && prev.prenom !== '-' ? prev.prenom : data.prenom || prev.prenom || '',
              telephone: prev.telephone || data.telephones?.[0] || '',
              email: prev.email || data.emails?.[0] || '',
              adresse: prev.adresse || data.adresse || '',
              cp: prev.cp || data.code_postal || '',
              ville: prev.ville || data.ville || ''
            }));
            // Mettre à jour le télépro assigné si présent dans les données pipeline
            if (data.telepro_assigne) {
              setTelepro_assigne(data.telepro_assigne);
            }
          }
        } catch (e) {
          console.error("Erreur chargement données pipeline:", e);
        }
      };
      loadFullData();
    }
  }, [client]);
  
  // Charger la configuration des champs et les données complètes du Sheet
  useEffect(() => {
    if (client?.row_number) {
      // Charger la config des champs
      authFetch(`${API}/config/client-fields`)
        .then(res => res.json())
        .then(data => setClientFieldsConfig(data))
        .catch(e => console.error("Erreur chargement config champs:", e));
      
      // Charger la config des onglets dynamiques
      authFetch(`${API}/config/dynamic-tabs`)
        .then(res => res.json())
        .then(data => setDynamicTabsConfig(data))
        .catch(e => console.error("Erreur chargement config onglets:", e));
      
      // Charger les champs personnalisés depuis MongoDB
      loadCustomFields();
      
      // Charger les données complètes du client depuis MongoDB
      setLoadingSheetData(true);
      const byRowUrl = client._clientId 
        ? `${API}/pipeline/client/by-row/${encodeURIComponent(client._clientId)}`
        : `${API}/pipeline/client/by-row/${client.row_number}`;
      authFetch(byRowUrl)
        .then(res => res.json())
        .then(data => {
          console.log("📋 Données client by-row chargées:", data);
          // Les données sont déjà au bon format, on les utilise directement
          if (data.client_details) {
            setSheetData(data.client_details);
            // Mettre à jour rdvFormData avec les données Qhare si pas déjà rempli
            // Utiliser tel_qhare en priorité si tel_client est vide
            const telephone = data.client_details.tel_client || data.client_details.tel_qhare || data.client_details.tel_secondaire || '';
            setRdvFormData(prev => ({
              ...prev,
              telephone: prev.telephone || telephone,
              telephoneFixe: prev.telephoneFixe || data.client_details.tel_fixe_qhare || '',
              email: prev.email || data.client_details.email_qhare || '',
              adresse: prev.adresse || data.client_details.adresse || '',
              cp: prev.cp || data.client_details.code_postal || '',
              ville: prev.ville || data.client_details.ville || ''
            }));
            
            // Charger les personnes de confiance si présentes dans client_details
            if (data.client_details.confiance_1_nom || data.client_details.confiance_2_nom) {
              const loadedPersonnes = [];
              // Personne 1
              if (data.client_details.confiance_1_nom) {
                loadedPersonnes.push({
                  nom: data.client_details.confiance_1_nom || '',
                  prenom: data.client_details.confiance_1_prenom || '',
                  telPortable: data.client_details.confiance_1_tel_portable || '',
                  telFixe: data.client_details.confiance_1_tel_fixe || '',
                  email: data.client_details.confiance_1_email || '',
                  lienGroupe: data.client_details.confiance_1_lien_groupe || '',
                  lienType: data.client_details.confiance_1_lien_type || '',
                  notes: data.client_details.confiance_1_notes || '',
                  isPrincipal: true
                });
              }
              // Personne 2
              if (data.client_details.confiance_2_nom) {
                loadedPersonnes.push({
                  nom: data.client_details.confiance_2_nom || '',
                  prenom: data.client_details.confiance_2_prenom || '',
                  telPortable: data.client_details.confiance_2_tel_portable || '',
                  telFixe: data.client_details.confiance_2_tel_fixe || '',
                  email: data.client_details.confiance_2_email || '',
                  lienGroupe: data.client_details.confiance_2_lien_groupe || '',
                  lienType: data.client_details.confiance_2_lien_type || '',
                  notes: data.client_details.confiance_2_notes || '',
                  isPrincipal: loadedPersonnes.length === 0
                });
              }
              if (loadedPersonnes.length > 0) {
                setPersonnesConfiance(loadedPersonnes);
              }
            }
          } else {
            // Construire client_details à partir des données retournées
            const newSheetData = {
              id_qhare: client.data?.['ID QHARE'] || data.id_qhare || '',
              nom: data.nom || client.data?.['CLIENT NOM'] || '',
              prenom: data.prenom || client.data?.['PRÉNOM'] || '',
              dpt: data.dpt || client.data?.['DPT'] || '',
              tel_client: data.telephones?.[0] || '',
              tel_secondaire: data.telephones?.[1] || '',
              email_qhare: data.emails?.[0] || '',
              adresse: data.adresse || '',
              code_postal: data.code_postal || '',
              ville: data.ville || '',
              telepro: data.telepro || '',
              technicien: data.technicien || '',
              source: data.source || '',
              maison_app: client.data?.['MAISON / APP'] || '',
              logement: client.data?.['LOGEMENT'] || '',
              categorie: client.data?.['Catégorie '] || '',
            };
            setSheetData(newSheetData);
            // Mettre à jour rdvFormData
            setRdvFormData(prev => ({
              ...prev,
              telephone: prev.telephone || newSheetData.tel_qhare || newSheetData.tel_client || '',
              telephoneFixe: prev.telephoneFixe || newSheetData.tel_fixe_qhare || '',
              email: prev.email || newSheetData.email_qhare || '',
              adresse: prev.adresse || newSheetData.adresse || '',
              cp: prev.cp || newSheetData.code_postal || '',
              ville: prev.ville || newSheetData.ville || ''
            }));
          }
        })
        .catch(e => console.error("Erreur chargement données client:", e))
        .finally(() => setLoadingSheetData(false));
    }
  }, [client?.row_number, loadCustomFields]);
  
  // Recherche d'adresse avec Nominatim (OpenStreetMap) - gratuit
  const searchAddress = async (query) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    
    setLoadingAddress(true);
    try {
      const response = await authFetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&countrycodes=fr&format=json&addressdetails=1&limit=5`,
        { headers: { 'Accept-Language': 'fr' } }
      );
      const data = await response.json();
      
      const suggestions = data.map(item => ({
        display: item.display_name,
        adresse: item.address?.road || item.address?.pedestrian || item.address?.hamlet || '',
        numero: item.address?.house_number || '',
        cp: item.address?.postcode || '',
        ville: item.address?.city || item.address?.town || item.address?.village || item.address?.municipality || '',
        full: `${item.address?.house_number || ''} ${item.address?.road || item.address?.pedestrian || ''}`.trim()
      }));
      
      setAddressSuggestions(suggestions);
      setShowSuggestions(true);
    } catch (err) {
      console.error("Erreur recherche adresse:", err);
    }
    setLoadingAddress(false);
  };
  
  // Sélectionner une adresse suggérée
  const selectAddress = async (suggestion) => {
    const newAdresse = suggestion.full || suggestion.adresse;
    const newCp = suggestion.cp;
    const newVille = suggestion.ville;
    
    console.log("📍 selectAddress appelé:", { newAdresse, newCp, newVille, clientRowNumber: client?.row_number });
    
    setRdvFormData(prev => ({
      ...prev,
      adresse: newAdresse,
      cp: newCp,
      ville: newVille
    }));
    setShowSuggestions(false);
    setAddressSuggestions([]);
    
    // Sauvegarder automatiquement l'adresse dans Google Sheet
    if (client?.row_number && newAdresse) {
      try {
        console.log("📍 Sauvegarde adresse pour client row_" + client.row_number);
        const resp = await authFetch(`${API}/pipeline/client/row_${cid(client)}/update-fields`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            adresse: newAdresse,
            code_postal: newCp,
            ville: newVille
          })
        });
        const data = await resp.json();
        console.log("✅ Adresse sauvegardée automatiquement:", data);
      } catch (e) { 
        console.error("❌ Erreur sauvegarde adresse:", e); 
      }
    } else {
      console.warn("⚠️ Impossible de sauvegarder: client.row_number non défini", client);
    }
  };
  
  // Vérifier si les champs requis pour RDV sont remplis
  const isRdvFormComplete = () => {
    return (
      rdvFormData.nom?.trim() &&
      rdvFormData.prenom?.trim() &&
      rdvFormData.telephone?.trim() &&
      rdvFormData.adresse?.trim() &&
      rdvFormData.ville?.trim()
    );
  };
  
  // Sauvegarder les modifications et lancer la prise de RDV
  const handlePrendreRdv = async () => {
    if (!isRdvFormComplete()) return;
    
    setSaving(true);
    try {
      // Sauvegarder les champs modifiés dans Google Sheet
      const fieldsToUpdate = [
        { title: 'CLIENT NOM', value: rdvFormData.nom },
        { title: 'PRÉNOM', value: rdvFormData.prenom },
        { title: 'TEL CLIENT', value: rdvFormData.telephone },
        { title: 'ADRESSE', value: rdvFormData.adresse },
        { title: 'CP', value: rdvFormData.cp },
        { title: 'VILLE', value: rdvFormData.ville }
      ];
      
      for (const field of fieldsToUpdate) {
        const col = columns.find(c => c.title === field.title);
        if (col && field.value) {
          await axios.patch(`${API}/clients/${client.row_number}`, {
            field_index: col.index,
            value: field.value
          });
        }
      }
      
      // Appeler la fonction de prise de RDV
      if (onPrendreRdv) {
        onPrendreRdv(client.row_number, rdvFormData);
      }
      
    } catch (err) {
      console.error("Erreur sauvegarde:", err);
      alert("Erreur lors de la sauvegarde des informations");
    }
    setSaving(false);
  };

  // Construire le client_id stable à partir des données du client
  const getClientId = () => {
    if (!client) return null;
    const idQhare = client.data?.['ID QHARE'];
    if (idQhare && idQhare.trim()) {
      return idQhare.trim();
    }
    const nom = client.data?.['CLIENT NOM'] || client.nom || '';
    const prenom = client.data?.['PRÉNOM'] || client.prenom || '';
    const dpt = client.data?.['DPT'] || client.dpt || '';
    return `npd:${nom}|${prenom}|${dpt}`;
  };

  // Charger l'historique des échanges du client
  useEffect(() => {
    const clientId = getClientId();
    if (activeTab === 'exchanges' && clientId) {
      setLoadingExchanges(true);
      authFetch(`${getApiUrl()}/api/clients/${encodeURIComponent(clientId)}/history`)
        .then(res => res.json())
        .then(data => {
          setExchanges(data.history || []);
        })
        .catch(err => console.error("Erreur chargement historique échanges:", err))
        .finally(() => setLoadingExchanges(false));
    }
  }, [activeTab, client]);

  // Charger les factures du client
  useEffect(() => {
    const clientId = getClientId();
    if (activeTab === 'factures' && clientId) {
      setLoadingFactures(true);
      authFetch(`${getApiUrl()}/api/clients/${encodeURIComponent(clientId)}/factures`)
        .then(res => res.json())
        .then(data => {
          setFactures(data.factures || []);
          setFacturesTotaux(data.totaux || { count: 0, total_ht: 0, total_ttc: 0 });
        })
        .catch(err => console.error("Erreur chargement factures:", err))
        .finally(() => setLoadingFactures(false));
    }
  }, [activeTab, client]);

  // Charger les encaissements du client
  useEffect(() => {
    const clientId = getClientId();
    if (activeTab === 'encaissements' && clientId) {
      setLoadingEncaissements(true);
      const token = localStorage.getItem('auth_token');
      authFetch(`${getApiUrl()}/api/compta-new/client-encaissements/${encodeURIComponent(clientId)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(data => {
          setClientEncaissements({
            encaissements: data.encaissements || [],
            total: data.total || 0,
            count: data.count || 0
          });
        })
        .catch(err => console.error("Erreur chargement encaissements:", err))
        .finally(() => setLoadingEncaissements(false));
    }
  }, [activeTab, client]);

  // Charger le count WhatsApp pour le badge
  useEffect(() => {
    if (client?.client_id) {
      authFetch(`${API}/whatsapp/client/${client.client_id}/messages?limit=1`)
        .then(r => r.json())
        .then(data => setWhatsappMsgCount(data.total || 0))
        .catch(() => setWhatsappMsgCount(0));
    }
  }, [client?.client_id]);

  // Charger l'historique des RDV du client
  useEffect(() => {
    if (activeTab === 'rdv-history' && client?.row_number) {
      setLoadingRdvHistory(true);
      authFetch(`${getApiUrl()}/api/pipeline/client/${client.row_number}/rdv-history`)
        .then(res => res.json())
        .then(data => {
          setRdvHistory(data.rdv_history || []);
          setRdvSupprimes(data.rdv_supprimes || []);
        })
        .catch(err => console.error("Erreur chargement historique RDV:", err))
        .finally(() => setLoadingRdvHistory(false));
    }
  }, [activeTab, client]);

  // Charger les rappels du client
  useEffect(() => {
    if (activeTab === 'rappels' && client?.row_number) {
      setLoadingRappels(true);
      authFetch(`${getApiUrl()}/api/pipeline/client/${client.row_number}/rappels`)
        .then(res => res.json())
        .then(data => {
          setClientRappels(data.rappels || []);
        })
        .catch(err => console.error("Erreur chargement rappels:", err))
        .finally(() => setLoadingRappels(false));
    }
  }, [activeTab, client]);

  // Charger le statut pipeline et les étapes disponibles
  useEffect(() => {
    if (client?.row_number) {
      setLoadingPipelineStage(true);
      Promise.all([
        authFetch(`${getApiUrl()}/api/leads/${client.row_number}/pipeline-stage`).then(r => r.json()),
        authFetch(`${getApiUrl()}/api/config/pipeline/etapes`).then(r => r.json())
      ])
        .then(([stageData, etapesData]) => {
          setPipelineStage(stageData.stage_info || { id: 'lead', nom: 'Lead', color: '#3B82F6' });
          const allStages = etapesData?.etapes || etapesData || [];
          setPipelineStages(allStages.filter(s => s.actif !== false));
        })
        .catch(err => console.error("Erreur chargement pipeline:", err))
        .finally(() => setLoadingPipelineStage(false));
    }
  }, [client]);

  // Charger les activités (historique)
  useEffect(() => {
    if (activeTab === 'historique' && client?.row_number) {
      setLoadingActivities(true);
      authFetch(`${getApiUrl()}/api/leads/${client.row_number}/activities`)
        .then(res => res.json())
        .then(data => {
          setActivities(data.activities || []);
        })
        .catch(err => console.error("Erreur chargement activités:", err))
        .finally(() => setLoadingActivities(false));
    }
  }, [activeTab, client]);

  // Fonction pour changer le statut pipeline
  const handlePipelineStageChange = async (newStageId) => {
    if (!client?.row_number || changingStage) return;
    setChangingStage(true);
    try {
      const res = await authFetch(`${getApiUrl()}/api/leads/${client.row_number}/pipeline-stage`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          new_stage: newStageId,
          user: authUser?.email || authUser?.prenom || 'Utilisateur'
        })
      });
      const data = await res.json();
      if (data.success) {
        const newStage = pipelineStages.find(s => s.id === newStageId);
        setPipelineStage(newStage);
        setShowStageDropdown(false);
        // Rafraîchir les activités si on est sur cet onglet
        if (activeTab === 'historique') {
          const actRes = await authFetch(`${getApiUrl()}/api/leads/${client.row_number}/activities`);
          const actData = await actRes.json();
          setActivities(actData.activities || []);
        }
      }
    } catch (err) {
      console.error("Erreur changement statut:", err);
    }
    setChangingStage(false);
  };

  if (!client) return null;

  const handleEdit = (col, value) => {
    setEditingField(col);
    setEditValue(value || "");
  };

  const handleSave = async () => {
    if (!editingField) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/clients/${client.row_number}`, {
        field_index: editingField.index,
        value: editValue
      });
      onUpdate();
      setEditingField(null);
    } catch (err) {
      alert("Erreur lors de la mise à jour");
    }
    setSaving(false);
  };

  const getDropdownOptions = (title) => {
    // Check if this field has dropdown options
    for (const [key, values] of Object.entries(dropdowns)) {
      if (title.includes(key) || key.includes(title.split(' ')[0])) {
        return values;
      }
    }
    return null;
  };

  // Group columns by category
  const groupColumns = () => {
    const groups = {
      'Informations Client': [],
      'Suivi': [],
      'Dates': [],
      'Montants': [],
      'Autres': []
    };
    
    columns.forEach(col => {
      const title = col.title.toLowerCase();
      if (title.includes('nom') || title.includes('prénom') || title.includes('dpt') || title.includes('logement') || title.includes('catégorie') || title.includes('tel')) {
        groups['Informations Client'].push(col);
      } else if (title.includes('date') || title.includes('rdv')) {
        groups['Dates'].push(col);
      } else if (title.includes('montant') || title.includes('€') || title.includes('ht') || title.includes('ttc') || title.includes('rac')) {
        groups['Montants'].push(col);
      } else if (title.includes('état') || title.includes('statut') || title.includes('résultat') || title.includes('confirmé')) {
        groups['Suivi'].push(col);
      } else {
        groups['Autres'].push(col);
      }
    });
    
    return groups;
  };

  const groups = groupColumns();

  // Get phone number from client data — prioritize CRM (telephones) over Sheet (TEL CLIENT)
  const crmPhones = client.telephones || [];
  const crmPhone = client.telephone || '';
  const sheetPhone = client.data?.['TEL CLIENT'] || client.data?.['TEL'] || client.data?.['TÉLÉPHONE'] || '';
  // Normalize: 33xxx -> 0xxx
  const normalizePhone = (p) => {
    if (!p) return '';
    let n = p.replace(/[\s.\-]/g, '');
    if (n.startsWith('33') && n.length === 11) n = '0' + n.slice(2);
    if (n.startsWith('+33')) n = '0' + n.slice(3);
    return n;
  };
  // Build unique phone list: CRM first, then sheet
  const allPhones = [...new Set([
    ...crmPhones.map(normalizePhone),
    normalizePhone(crmPhone),
    normalizePhone(sheetPhone),
  ].filter(p => p && p.length >= 10))];
  const phoneNumber = allPhones[0] || '';
  const clientName = `${client.data['CLIENT NOM'] || ''} ${client.data['PRÉNOM'] || ''}`.trim();

  // Fonction pour appeler via Ringover
  const handleCallClient = (e) => {
    e.stopPropagation();
    if (window.ringoverDial && phoneNumber) {
      window.ringoverDial(phoneNumber);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        data-testid="client-modal"
        className="bg-slate-900 border border-slate-700 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700 bg-slate-800">
          <div className="flex items-center gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-white">
                  {client.data['CLIENT NOM']} {client.data['PRÉNOM']}
                </h2>
                {/* Dropdown Statut Pipeline */}
                <div className="relative">
                  <button
                    data-testid="pipeline-stage-dropdown"
                    onClick={() => setShowStageDropdown(!showStageDropdown)}
                    disabled={loadingPipelineStage || changingStage}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      loadingPipelineStage || changingStage ? 'opacity-50 cursor-wait' : 'hover:opacity-80'
                    }`}
                    style={{ 
                      backgroundColor: `${pipelineStage?.color || '#3B82F6'}20`,
                      borderColor: `${pipelineStage?.color || '#3B82F6'}40`,
                      color: pipelineStage?.color || '#3B82F6',
                      borderWidth: '1px'
                    }}
                  >
                    {changingStage ? (
                      <RefreshCw className="w-3 h-3 animate-spin" />
                    ) : null}
                    <span>{pipelineStage?.nom || 'Lead'}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showStageDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Dropdown menu */}
                  {showStageDropdown && (
                    <div className="absolute top-full left-0 mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl z-[100] min-w-[200px] py-1 max-h-[300px] overflow-y-auto">
                      {pipelineStages.map((stage) => (
                        <button
                          key={stage.id}
                          onClick={() => handlePipelineStageChange(stage.id)}
                          disabled={stage.id === pipelineStage?.id}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                            stage.id === pipelineStage?.id 
                              ? 'bg-slate-700/50 cursor-default' 
                              : 'hover:bg-slate-700'
                          }`}
                        >
                          <span 
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: stage.color || '#3B82F6' }}
                          />
                          <span className={stage.id === pipelineStage?.id ? 'text-slate-400' : 'text-white'}>
                            {stage.nom}
                          </span>
                          {stage.id === pipelineStage?.id && (
                            <Check className="w-4 h-4 ml-auto text-emerald-400" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <p className="text-slate-400 text-sm">
                Dép. {client.data['DPT']} • {client.data['LOGEMENT']} • Num client : {client.client_details?.id_qhare || client.data?.['ID QHARE'] || `L${client.row_number}`}
                {phoneNumber && (
                  <button 
                    onClick={handleCallClient}
                    className="ml-2 text-emerald-400 hover:text-emerald-300 hover:underline transition-colors"
                    title="Cliquer pour appeler avec Ringover"
                  >
                    📞 {phoneNumber}
                  </button>
                )}
              </p>
            </div>
            {/* Indicateurs d'éligibilité */}
            {eligibilite.checked && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
                eligibilite.resultat === 'tres_modeste' 
                  ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400'
                  : eligibilite.resultat === 'modeste'
                  ? 'bg-blue-500/20 border-blue-500/40 text-blue-400'
                  : eligibilite.resultat?.includes('_conditionnel')
                  ? 'bg-amber-500/20 border-amber-500/40 text-amber-400'
                  : 'bg-red-500/20 border-red-500/40 text-red-400'
              }`}>
                {eligibilite.resultat === 'tres_modeste' && (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="text-sm font-medium">MPA Très Modeste (70%)</span>
                    {eligibilite.montantRestant > 0 && eligibilite.montantRestant < eligibilite.enveloppeMax && (
                      <span className="text-xs bg-emerald-500/30 px-1.5 py-0.5 rounded">Reste: {eligibilite.montantRestant.toLocaleString()}€</span>
                    )}
                  </>
                )}
                {eligibilite.resultat === 'modeste' && (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="text-sm font-medium">MPA Modeste (50%)</span>
                    {eligibilite.montantRestant > 0 && eligibilite.montantRestant < eligibilite.enveloppeMax && (
                      <span className="text-xs bg-blue-500/30 px-1.5 py-0.5 rounded">Reste: {eligibilite.montantRestant.toLocaleString()}€</span>
                    )}
                  </>
                )}
                {eligibilite.resultat === 'tres_modeste_conditionnel' && (
                  <>
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm font-medium">MPA Très Modeste - SOUS CONDITION</span>
                  </>
                )}
                {eligibilite.resultat === 'modeste_conditionnel' && (
                  <>
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm font-medium">MPA Modeste - SOUS CONDITION</span>
                  </>
                )}
                {eligibilite.resultat === 'non_eligible' && (
                  <>
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm font-medium">Hors MPA</span>
                  </>
                )}
              </div>
            )}
          </div>
          
          {/* Côté droit : Attribution Télépro + Bouton Fermer */}
          <div className="flex items-center gap-4">
            {/* Attribution Télépro - OBLIGATOIRE */}
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
              !telepro_assigne 
                ? 'bg-red-500/10 border-red-500/50' 
                : 'bg-slate-700/50 border-slate-600'
            }`}>
              <span className="text-slate-300 text-sm font-medium">Télépro :</span>
              <select
                value={telepro_assigne}
                onChange={async (e) => {
                  const newValue = e.target.value;
                  setTelepro_assigne(newValue);
                  setSavingTeleproAssign(true);
                  try {
                    const response = await authFetch(`${API}/webhook/leads/update-field`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        row_number: client.row_number,
                        field: 'TÉLÉPRO ASSIGNÉ',
                        value: newValue
                      })
                    });
                    if (response.ok) {
                      toast.success(`Lead assigné à ${newValue}`);
                    } else {
                      toast.error('Erreur lors de l\'attribution');
                    }
                  } catch (err) {
                    console.error('Erreur attribution:', err);
                    toast.error('Erreur de connexion');
                  } finally {
                    setSavingTeleproAssign(false);
                  }
                }}
                className={`px-3 py-1.5 bg-slate-800 border rounded-lg text-white text-sm focus:outline-none min-w-[160px] ${
                  !telepro_assigne 
                    ? 'border-red-500 focus:border-red-400' 
                    : 'border-slate-600 focus:border-blue-500'
                }`}
                data-testid="telepro-select-header"
              >
                <option value="">Sélectionner...</option>
                {telepro_assigne && !teleprosList.find(tp => tp.nom === telepro_assigne) && (
                  <option value={telepro_assigne}>{telepro_assigne}</option>
                )}
                {teleprosList.map(tp => (
                  <option key={tp.id} value={tp.nom}>
                    {tp.nom}
                  </option>
                ))}
                {authUser?.name && !teleprosList.find(tp => tp.nom === authUser.name) && (
                  <option value={authUser.name}>📍 {authUser.name} (moi)</option>
                )}
              </select>
              {savingTeleproAssign && (
                <RefreshCw className="w-4 h-4 animate-spin text-blue-400" />
              )}
              {!telepro_assigne && authUser?.name && (
                <button
                  onClick={async () => {
                    setTelepro_assigne(authUser.name);
                    setSavingTeleproAssign(true);
                    try {
                      const response = await authFetch(`${API}/webhook/leads/update-field`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          row_number: client.row_number,
                          field: 'TÉLÉPRO ASSIGNÉ',
                          value: authUser.name
                        })
                      });
                      if (response.ok) {
                        toast.success('Lead assigné à vous !');
                      }
                    } catch (err) {
                      console.error('Erreur:', err);
                    } finally {
                      setSavingTeleproAssign(false);
                    }
                  }}
                  className="px-2 py-1 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-lg transition-colors whitespace-nowrap"
                  data-testid="self-assign-btn-header"
                >
                  Moi
                </button>
              )}
            </div>
            {!telepro_assigne && (
              <span className="text-red-400 text-xs">Obligatoire</span>
            )}
            
            <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Tabs - scrollable horizontally - DYNAMIC from config */}
        <div className="flex border-b border-slate-700 bg-slate-850 overflow-x-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent">
          {(() => {
            const ICON_MAP = { FileText, Mail, Phone, Calendar, Receipt, DollarSign, CalendarPlus, Settings: FileText, Bell, Activity, MessageSquare, CheckCircle2, FileEdit, Layers: FileText };
            const COLOR_MAP = {
              blue: { active: 'text-blue-400 border-b-2 border-blue-400 bg-slate-800/50', idle: 'text-slate-400 hover:text-white hover:bg-slate-800/30' },
              indigo: { active: 'text-indigo-400 border-b-2 border-indigo-400 bg-indigo-800/20', idle: 'text-slate-400 hover:text-white hover:bg-slate-800/30' },
              emerald: { active: 'text-emerald-400 border-b-2 border-emerald-400 bg-emerald-800/20', idle: 'text-slate-400 hover:text-white hover:bg-slate-800/30' },
              cyan: { active: 'text-cyan-400 border-b-2 border-cyan-400 bg-cyan-800/20', idle: 'text-slate-400 hover:text-white hover:bg-slate-800/30' },
              purple: { active: 'text-purple-400 border-b-2 border-purple-400 bg-purple-800/20', idle: 'text-slate-400 hover:text-white hover:bg-slate-800/30' },
              amber: { active: 'text-amber-400 border-b-2 border-amber-400 bg-amber-800/20', idle: 'text-slate-400 hover:text-white hover:bg-slate-800/30' },
              green: { active: 'text-green-400 border-b-2 border-green-400 bg-green-800/20', idle: 'text-slate-400 hover:text-white hover:bg-slate-800/30' },
              orange: { active: 'text-orange-400 border-b-2 border-orange-400 bg-orange-800/20', idle: 'text-slate-400 hover:text-white hover:bg-slate-800/30' },
            };
            const BADGE_DATA = {
              exchanges_count: exchanges.length,
              rdv_count: rdvHistory.length,
              rappels_count: clientRappels.length,
              activities_count: activities.length,
              factures_count: facturesTotaux.count,
              encaissements_count: clientEncaissements?.count,
              whatsapp_count: whatsappMsgCount,
            };

            const activeTabs = (dynamicTabsConfig?.tabs || [])
              .filter(t => t.enabled)
              .sort((a, b) => a.order - b.order);

            // Fallback: if no config loaded yet, show default tabs
            const tabsToRender = activeTabs.length > 0 ? activeTabs : [
              { id: 'infos', label: 'Informations', icon: 'FileText', color: 'blue' },
              { id: 'tasks_internes', label: 'Taches internes', icon: 'MessageSquare', color: 'indigo' },
              { id: 'eligibilites', label: 'Eligibilites & TS', icon: 'CheckCircle2', color: 'emerald' },
              { id: 'exchanges', label: 'Echanges mails', icon: 'Mail', color: 'cyan' },
              { id: 'history', label: 'Appels & SMS', icon: 'Phone', color: 'blue' },
              { id: 'rdv-history', label: 'RDV', icon: 'Calendar', color: 'purple' },
              { id: 'rappels', label: 'Rappels', icon: 'Bell', color: 'amber' },
              { id: 'historique', label: 'Historique', icon: 'Activity', color: 'purple' },
              { id: 'factures', label: 'Factures Fournisseurs', icon: 'Receipt', color: 'blue' },
              { id: 'encaissements', label: 'Encaissements', icon: 'DollarSign', color: 'green' },
              { id: 'rdv', label: 'Prendre RDV', icon: 'CalendarPlus', color: 'emerald' },
              { id: 'devis', label: 'Devis', icon: 'FileEdit', color: 'orange' },
              { id: 'whatsapp_exchanges', label: 'Echanges WhatsApp', icon: 'MessageSquare', color: 'emerald', badge_field: 'whatsapp_count' },
            ];

            return tabsToRender.map(tab => {
              const Icon = ICON_MAP[tab.icon] || FileText;
              const colors = COLOR_MAP[tab.color] || COLOR_MAP.blue;
              const isActive = activeTab === tab.id;
              const badgeValue = tab.badge_field ? BADGE_DATA[tab.badge_field] : null;
              const showRdvBadge = tab.id === 'rdv' && !isRdvFormComplete();

              return (
                <button
                  key={tab.id}
                  data-testid={`tab-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                    isActive ? colors.active : (tab.id === 'rdv' ? 'text-emerald-400 hover:text-emerald-300 hover:bg-emerald-800/10' : colors.idle)
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {badgeValue > 0 && (
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      isActive ? 'bg-white/20' : `bg-${tab.color}-500/20 text-${tab.color}-400`
                    }`}>
                      {badgeValue}
                    </span>
                  )}
                  {showRdvBadge && (
                    <span className="bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full text-xs">!</span>
                  )}
                </button>
              );
            });
          })()}
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          {activeTab === 'infos' ? (
            <div className="p-5">
              {/* Bandeau Reprendre la qualification */}
              {client?.qualification_progress?.current_step && onResumeQualification && (
                <div data-testid="resume-qualification-banner" className="mb-4 bg-gradient-to-r from-amber-900/30 to-orange-900/30 border border-amber-500/40 rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <PlayCircle className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-amber-300">Qualification en cours</p>
                      <p className="text-xs text-amber-400/70">
                        Arrêtée à l'étape {client.qualification_progress.current_step}/5
                      </p>
                    </div>
                  </div>
                  <button
                    data-testid="btn-resume-qualification"
                    onClick={() => onResumeQualification({
                      row_number: client.row_number,
                      nom: client.nom || client.data?.['CLIENT NOM'] || '',
                      prenom: client.prenom || client.data?.['PRÉNOM'] || '',
                      telephone: client.telephone || client.data?.['TEL CLIENT'] || '',
                      email: client.email || client.data?.['EMAIL'] || '',
                      code_postal: client.code_postal || client.data?.['CODE POSTAL'] || '',
                      ville: client.ville || client.data?.['VILLE'] || '',
                      adresse: client.adresse || client.data?.['ADRESSE'] || '',
                    })}
                    className="px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                  >
                    <PlayCircle className="w-4 h-4" />
                    Reprendre
                  </button>
                </div>
              )}
              {/* SIMULATEUR MPA RETIRÉ - Utiliser "Nouvelle éligibilité" dans l'onglet Éligibilités & TS */}
              {/* Bloc Informations de Contact - Éditable avec autocomplétion */}
              <div className="mb-6 bg-gradient-to-r from-slate-800/80 to-slate-900/80 border border-slate-600 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-emerald-400 uppercase tracking-wider flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Coordonnées & Adresse
                    {(!rdvFormData.telephone || !rdvFormData.adresse || !rdvFormData.ville) && (
                      <span className="bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded text-xs normal-case">
                        Incomplet
                      </span>
                    )}
                  </h3>
                </div>
                
                {/* Nom et Prénom */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Nom</label>
                    <div className="px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm">
                      {sheetData?.nom || client.data?.['CLIENT NOM'] || '-'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Prénom</label>
                    <div className="px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm">
                      {sheetData?.prenom || client.data?.['PRÉNOM'] || '-'}
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {/* Téléphone portable */}
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Téléphone portable *</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <input
                        type="tel"
                        value={rdvFormData.telephone || sheetData?.tel_qhare || sheetData?.tel_client || sheetData?.tel_secondaire || ''}
                        onChange={e => setRdvFormData(prev => ({ ...prev, telephone: e.target.value }))}
                        className="w-full pl-10 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-emerald-500 focus:outline-none"
                        placeholder="06 12 34 56 78"
                      />
                    </div>
                  </div>
                  
                  {/* Téléphone fixe */}
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Téléphone fixe</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <input
                        type="tel"
                        value={rdvFormData.telephoneFixe || sheetData?.tel_fixe_qhare || ''}
                        onChange={e => setRdvFormData(prev => ({ ...prev, telephoneFixe: e.target.value }))}
                        className="w-full pl-10 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-emerald-500 focus:outline-none"
                        placeholder="01 23 45 67 89"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="mb-4">
                  {/* Email */}
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <input
                        type="email"
                        value={rdvFormData.email || sheetData?.email_qhare || ''}
                        onChange={e => setRdvFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full pl-10 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:border-emerald-500 focus:outline-none"
                        placeholder="exemple@email.com"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Adresse avec autocomplétion */}
                <div className="relative mb-4">
                  <label className="block text-xs text-slate-400 mb-1">Adresse * (commencez à taper pour l'autocomplétion)</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="text"
                      value={rdvFormData.adresse}
                      onChange={e => {
                        setRdvFormData(prev => ({ ...prev, adresse: e.target.value }));
                        searchAddress(e.target.value);
                      }}
                      onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                      className="w-full pl-10 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-emerald-500 focus:outline-none"
                      placeholder="Ex: 12 rue de la Paix, Paris"
                    />
                    {loadingAddress && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
                      </div>
                    )}
                  </div>
                  
                  {/* Suggestions d'adresses */}
                  {showSuggestions && addressSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                      {addressSuggestions.map((suggestion, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => selectAddress(suggestion)}
                          className="w-full px-3 py-2 text-left hover:bg-slate-700 border-b border-slate-700 last:border-b-0 transition-colors"
                        >
                          <p className="text-sm text-white">{suggestion.full || suggestion.adresse}</p>
                          <p className="text-xs text-slate-400">{suggestion.cp} {suggestion.ville}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* CP et Ville */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Code Postal</label>
                    <input
                      type="text"
                      value={rdvFormData.cp}
                      onChange={e => setRdvFormData(prev => ({ ...prev, cp: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-emerald-500 focus:outline-none"
                      placeholder="75001"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-slate-400 mb-1">Ville *</label>
                    <input
                      type="text"
                      value={rdvFormData.ville}
                      onChange={e => setRdvFormData(prev => ({ ...prev, ville: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-emerald-500 focus:outline-none"
                      placeholder="Paris"
                    />
                  </div>
                </div>
                
                {/* Bouton Enregistrer */}
                <div className="flex items-center gap-3 mt-4">
                  <button
                    onClick={saveCoordonnees}
                    disabled={savingCoords}
                    className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-600 text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
                    data-testid="save-coords-btn"
                  >
                    {savingCoords ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Enregistrer les coordonnées
                      </>
                    )}
                  </button>
                  {/* Bouton Prendre RDV - visible si pas sous tutelle/curatelle et pas de date_r1 */}
                  {protectionJuridique === 'non' && !client?.date_r1 && onPrendreRdv && (() => {
                    const rdvCheck = canTakeRdv();
                    return (
                      <div className="relative group">
                        <button
                          onClick={() => {
                            if (rdvCheck.canTake) {
                              saveCoordonnees();
                              if (onPrendreRdv) onPrendreRdv(client.row_number, rdvFormData);
                            }
                          }}
                          disabled={!rdvCheck.canTake}
                          className={`px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                            rdvCheck.canTake
                              ? 'bg-blue-600 hover:bg-blue-500 text-white'
                              : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                          }`}
                          data-testid="prendre-rdv-coords-btn"
                        >
                          <CalendarPlus className="w-4 h-4" />
                          Prendre RDV
                        </button>
                        {!rdvCheck.canTake && (
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50">
                            <div className="bg-slate-800 border border-red-500/50 rounded-lg p-2 text-xs shadow-xl max-w-xs">
                              {!rdvCheck.hasAdresse && <p className="text-red-400">Renseignez l'adresse, le code postal et la ville</p>}
                              {!rdvCheck.hasContact && <p className="text-red-400">Renseignez au moins un n° de portable ou un email</p>}
                            </div>
                          </div>
                        )}
                        {rdvCheck.onlyEmail && rdvCheck.canTake && (
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50">
                            <div className="bg-slate-800 border border-amber-500/50 rounded-lg p-2 text-xs shadow-xl max-w-xs">
                              <p className="text-amber-400">Pas de portable — la confirmation sera envoyée par email uniquement</p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {coordsSaved && (
                    <span className="text-emerald-400 text-sm flex items-center gap-1">
                      <Check className="w-4 h-4" />
                      Sauvegardé !
                    </span>
                  )}
                </div>
              </div>
              
              {/* SECTION PERSONNES DE CONFIANCE */}
              <div id="section-personnes-confiance" className="mb-6 bg-gradient-to-r from-slate-800/60 to-slate-900/60 border border-amber-600/30 rounded-xl p-4">
                {/* Bandeau alerte si tutelle/curatelle */}
                {(protectionJuridique === 'tutelle' || protectionJuridique === 'curatelle') && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/40 rounded-lg animate-pulse">
                    <p className="text-red-400 text-sm font-medium">
                      Client sous {protectionJuridique} — renseignez le {protectionJuridique === 'tutelle' ? 'tuteur' : 'curateur'} ci-dessous avant de prendre RDV
                    </p>
                  </div>
                )}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-amber-400 uppercase tracking-wider flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Personne(s) de confiance
                  </h3>
                  {personnesConfiance.length < 2 && (
                    <button
                      onClick={() => setPersonnesConfiance(prev => [...prev, { nom: '', prenom: '', telPortable: '', telFixe: '', email: '', lienGroupe: '', lienType: '', notes: '', isPrincipal: false }])}
                      className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      Ajouter un contact
                    </button>
                  )}
                </div>
                
                {personnesConfiance.map((personne, index) => (
                  <div 
                    key={index} 
                    className={`mb-4 p-4 rounded-lg border ${personne.isPrincipal ? 'bg-amber-900/20 border-amber-500/40' : 'bg-slate-800/40 border-slate-600/40'}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {personne.isPrincipal ? (
                          <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                            ⭐ Contact principal
                          </span>
                        ) : (
                          <button
                            onClick={() => {
                              setPersonnesConfiance(prev => prev.map((p, i) => ({ ...p, isPrincipal: i === index })));
                            }}
                            className="text-xs text-slate-400 hover:text-amber-400 flex items-center gap-1"
                          >
                            Définir comme principal
                          </button>
                        )}
                      </div>
                      {personnesConfiance.length > 1 && (
                        <button
                          onClick={() => {
                            setPersonnesConfiance(prev => {
                              const newList = prev.filter((_, i) => i !== index);
                              // Si on supprime le principal, le premier devient principal
                              if (personne.isPrincipal && newList.length > 0) {
                                newList[0].isPrincipal = true;
                              }
                              return newList;
                            });
                          }}
                          className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" />
                          Supprimer
                        </button>
                      )}
                    </div>
                    
                    {/* Nom / Prénom */}
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Nom</label>
                        <input
                          type="text"
                          value={personne.nom}
                          onChange={e => {
                            const newVal = e.target.value;
                            setPersonnesConfiance(prev => prev.map((p, i) => i === index ? { ...p, nom: newVal } : p));
                          }}
                          className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-amber-500 focus:outline-none"
                          placeholder="Nom"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Prénom</label>
                        <input
                          type="text"
                          value={personne.prenom}
                          onChange={e => {
                            const newVal = e.target.value;
                            setPersonnesConfiance(prev => prev.map((p, i) => i === index ? { ...p, prenom: newVal } : p));
                          }}
                          className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-amber-500 focus:outline-none"
                          placeholder="Prénom"
                        />
                      </div>
                    </div>
                    
                    {/* Téléphones */}
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Tél portable</label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                          <input
                            type="tel"
                            value={personne.telPortable}
                            onChange={e => {
                              const newVal = e.target.value;
                              setPersonnesConfiance(prev => prev.map((p, i) => i === index ? { ...p, telPortable: newVal } : p));
                            }}
                            className="w-full pl-10 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-amber-500 focus:outline-none"
                            placeholder="06 12 34 56 78"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Tél fixe</label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                          <input
                            type="tel"
                            value={personne.telFixe}
                            onChange={e => {
                              const newVal = e.target.value;
                              setPersonnesConfiance(prev => prev.map((p, i) => i === index ? { ...p, telFixe: newVal } : p));
                            }}
                            className="w-full pl-10 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-amber-500 focus:outline-none"
                            placeholder="01 23 45 67 89"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Email */}
                    <div className="mb-3">
                      <label className="block text-xs text-slate-400 mb-1">Email</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <input
                          type="email"
                          value={personne.email}
                          onChange={e => {
                            const newVal = e.target.value;
                            setPersonnesConfiance(prev => prev.map((p, i) => i === index ? { ...p, email: newVal } : p));
                          }}
                          className="w-full pl-10 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-amber-500 focus:outline-none"
                          placeholder="email@exemple.com"
                        />
                      </div>
                    </div>
                    
                    {/* Lien avec le bénéficiaire - 2 sélecteurs */}
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Type de lien</label>
                        <select
                          value={personne.lienGroupe}
                          onChange={e => {
                            const newGroupe = e.target.value;
                            setPersonnesConfiance(prev => prev.map((p, i) => i === index ? { ...p, lienGroupe: newGroupe, lienType: '' } : p));
                          }}
                          className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none"
                        >
                          <option value="">Sélectionner...</option>
                          {Object.keys(liensOptions).map(groupe => (
                            <option key={groupe} value={groupe}>{groupe}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Lien précis</label>
                        <select
                          value={personne.lienType}
                          onChange={e => {
                            const newType = e.target.value;
                            setPersonnesConfiance(prev => prev.map((p, i) => i === index ? { ...p, lienType: newType } : p));
                          }}
                          disabled={!personne.lienGroupe}
                          className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none disabled:opacity-50"
                        >
                          <option value="">Sélectionner...</option>
                          {personne.lienGroupe && liensOptions[personne.lienGroupe]?.map(lien => (
                            <option key={lien} value={lien}>{lien}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {/* Notes */}
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Notes</label>
                      <textarea
                        value={personne.notes}
                        onChange={e => {
                          const newVal = e.target.value;
                          setPersonnesConfiance(prev => prev.map((p, i) => i === index ? { ...p, notes: newVal } : p));
                        }}
                        rows={2}
                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-amber-500 focus:outline-none resize-none"
                        placeholder="Ex: Disponible après 18h, ne pas appeler le weekend..."
                      />
                    </div>
                  </div>
                ))}
                
                {/* Bouton Enregistrer les personnes de confiance */}
                <div className="flex items-center gap-3 mt-4">
                  <button
                    onClick={savePersonnesConfiance}
                    disabled={savingConfiance}
                    className="px-6 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-slate-600 text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
                    data-testid="save-confiance-btn"
                  >
                    {savingConfiance ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Enregistrer les contacts
                      </>
                    )}
                  </button>
                  {/* Bouton Prendre RDV - visible si sous tutelle/curatelle ET personne de confiance renseignée */}
                  {(protectionJuridique === 'tutelle' || protectionJuridique === 'curatelle') && !client?.date_r1 && onPrendreRdv && (() => {
                    const rdvCheck = canTakeRdv();
                    const hasConfiance = personnesConfiance.some(p => p.nom && p.telPortable);
                    const canProceed = rdvCheck.canTake && hasConfiance;
                    return (
                      <div className="relative group">
                        <button
                          onClick={() => {
                            if (canProceed) {
                              savePersonnesConfiance();
                              saveCoordonnees();
                              if (onPrendreRdv) onPrendreRdv(client.row_number, rdvFormData);
                            }
                          }}
                          disabled={!canProceed}
                          className={`px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                            canProceed
                              ? 'bg-blue-600 hover:bg-blue-500 text-white'
                              : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                          }`}
                          data-testid="prendre-rdv-confiance-btn"
                        >
                          <CalendarPlus className="w-4 h-4" />
                          Prendre RDV
                        </button>
                        {!canProceed && (
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50">
                            <div className="bg-slate-800 border border-red-500/50 rounded-lg p-2 text-xs shadow-xl max-w-xs">
                              {!rdvCheck.hasAdresse && <p className="text-red-400">Renseignez l'adresse, le code postal et la ville</p>}
                              {!rdvCheck.hasContact && <p className="text-red-400">Renseignez au moins un n° de portable ou un email</p>}
                              {!hasConfiance && <p className="text-red-400">Renseignez le {protectionJuridique === 'tutelle' ? 'tuteur' : 'curateur'} (nom + téléphone)</p>}
                            </div>
                          </div>
                        )}
                        {rdvCheck.onlyEmail && canProceed && (
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50">
                            <div className="bg-slate-800 border border-amber-500/50 rounded-lg p-2 text-xs shadow-xl max-w-xs">
                              <p className="text-amber-400">Pas de portable — confirmation par email uniquement</p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {confianceSaved && (
                    <span className="text-amber-400 text-sm flex items-center gap-1">
                      <Check className="w-4 h-4" />
                      Sauvegardé !
                    </span>
                  )}
                </div>
                {/* Alerte tutelle/curatelle */}
                {(protectionJuridique === 'tutelle' || protectionJuridique === 'curatelle') && (
                  <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-red-400 text-sm">
                      Client sous <span className="font-bold">{protectionJuridique}</span> — le {protectionJuridique === 'tutelle' ? 'tuteur' : 'curateur'} doit être renseigné ci-dessus avant la prise de RDV.
                    </p>
                  </div>
                )}
              </div>
              
              {/* Section Informations Logement */}
              <LogementInfoSection clientId={client?.row_number} authUser={authUser} />
              <MandataireANAHSection clientId={client?.row_number} authUser={authUser} />
              
              {/* Section des champs configurés depuis Qhare */}
              {clientFieldsConfig && sheetData && (
                <div className="mb-6 bg-gradient-to-r from-slate-800/60 to-slate-900/60 border border-slate-600/50 rounded-xl p-4">
                  <h3 className="text-sm font-bold text-cyan-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Informations Complètes du Dossier
                    {loadingSheetData && <RefreshCw className="w-3 h-3 animate-spin text-slate-400" />}
                  </h3>
                  
                  {/* Champs déjà affichés dans Coordonnées & Adresse - à exclure */}
                  {(() => {
                    const excludedFields = ['nom', 'prenom', 'tel_client', 'email_qhare', 'adresse', 'code_postal', 'ville'];
                    
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(clientFieldsConfig.categories || {}).map(([catId, category]) => {
                          // Ne montrer que les catégories qui ont des champs activés avec des valeurs
                          // ET qui ne sont pas dans les champs exclus (déjà affichés)
                          const enabledFieldsWithValues = category.fields.filter(
                            field => field.enabled && sheetData[field.id] && !excludedFields.includes(field.id)
                          );
                          
                          if (enabledFieldsWithValues.length === 0) return null;
                          
                          return (
                            <div 
                              key={catId}
                              className="bg-slate-800/40 rounded-lg p-3 border border-slate-700/30"
                            >
                              <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2 flex items-center gap-1">
                                <span>{category.icon}</span>
                                {category.label}
                              </h4>
                              <div className="space-y-1">
                                {enabledFieldsWithValues.map(field => (
                                  <div 
                                    key={field.id} 
                                    className="flex justify-between items-center py-1 text-sm border-b border-slate-700/20 last:border-b-0"
                                  >
                                    <span className="text-slate-400 text-xs">{field.label}</span>
                                    <span 
                                      className="text-white font-medium text-right max-w-[60%] truncate" 
                                      title={sheetData[field.id]}
                                    >
                                      {sheetData[field.id]}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  
                  {Object.entries(clientFieldsConfig.categories || {}).every(([catId, category]) => {
                    const excludedFields = ['nom', 'prenom', 'tel_client', 'email_qhare', 'adresse', 'code_postal', 'ville'];
                    return !category.fields.some(f => f.enabled && sheetData[f.id] && !excludedFields.includes(f.id));
                  }) && (
                    <p className="text-slate-400 text-sm text-center py-4">
                      Aucun champ supplémentaire configuré
                    </p>
                  )}
                </div>
              )}
              
              {/* Autres colonnes par catégorie */}
              {Object.entries(groups).map(([groupName, groupCols]) => {
                // Filtrer les colonnes déjà affichées dans le bloc "Coordonnées & Adresse"
                const filteredCols = groupCols.filter(col => {
                  const title = col.title.toUpperCase();
                  return title !== 'ADRESSE' && 
                         title !== 'CP' && 
                         title !== 'VILLE' && 
                         title !== 'TEL CLIENT' && 
                         title !== 'TEL' &&
                         title !== 'EMAIL' &&
                         title !== 'EMAIL CLIENT';
                });
                if (filteredCols.length === 0) return null;
                return (
                  <div key={groupName} className="mb-6">
                    <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3">{groupName}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {filteredCols.map(col => {
                        const value = client.data[col.title] || "";
                        const dropdownOpts = getDropdownOptions(col.title);
                        const isEditing = editingField?.index === col.index;
                        const isAddressField = col.title.toUpperCase() === "ADRESSE";
                        
                        return (
                          <div key={col.index} className={`bg-slate-800/50 rounded-lg p-3 border border-slate-700/50 ${isAddressField && isEditing ? 'relative' : ''}`}>
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-xs text-slate-500 uppercase tracking-wider truncate">
                                {col.title}
                                {isAddressField && <span className="text-emerald-400 ml-1">(autocomplete)</span>}
                              </label>
                              <button 
                                onClick={() => handleEdit(col, value)}
                                className="p-1 hover:bg-slate-700 rounded opacity-50 hover:opacity-100"
                              >
                                <Edit2 className="w-3 h-3 text-slate-400" />
                              </button>
                            </div>
                            
                            {isEditing ? (
                              <div className="space-y-2">
                                <div className="flex gap-2">
                                  {dropdownOpts ? (
                                    <select
                                      value={editValue}
                                      onChange={e => setEditValue(e.target.value)}
                                      className="flex-1 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-sm"
                                    >
                                      <option value="">--</option>
                                      {dropdownOpts.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    <div className="flex-1 relative">
                                      <input
                                        type="text"
                                        value={editValue}
                                        onChange={e => {
                                          setEditValue(e.target.value);
                                          if (isAddressField && e.target.value.length >= 3) {
                                            searchAddress(e.target.value);
                                          }
                                        }}
                                        onFocus={() => isAddressField && addressSuggestions.length > 0 && setShowSuggestions(true)}
                                        className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-sm"
                                        placeholder={isAddressField ? "Tapez une adresse..." : ""}
                                      />
                                      {isAddressField && loadingAddress && (
                                        <RefreshCw className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 animate-spin" />
                                      )}
                                    </div>
                                  )}
                                  <button onClick={handleSave} disabled={saving} className="px-2 py-1 bg-blue-600 text-white rounded text-sm">
                                    {saving ? "..." : "OK"}
                                  </button>
                                  <button onClick={() => { setEditingField(null); setShowSuggestions(false); setAddressSuggestions([]); }} className="px-2 py-1 bg-slate-700 text-white rounded text-sm">✕</button>
                                </div>
                                
                                {/* Suggestions d'adresses */}
                                {isAddressField && showSuggestions && addressSuggestions.length > 0 && (
                                  <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                                    {addressSuggestions.map((suggestion, idx) => (
                                      <button
                                        key={idx}
                                        type="button"
                                        onClick={() => {
                                          setEditValue(suggestion.full || suggestion.adresse || suggestion.display);
                                          setShowSuggestions(false);
                                          setAddressSuggestions([]);
                                          // Auto-remplir CP et Ville si possible
                                          if (suggestion.cp) {
                                            const cpCol = columns.find(c => c.title === 'CP');
                                            if (cpCol) {
                                              axios.patch(`${API}/clients/${client.row_number}`, {
                                                field_index: cpCol.index,
                                                value: suggestion.cp
                                              }).catch(e => console.error(e));
                                            }
                                          }
                                          if (suggestion.ville) {
                                            const villeCol = columns.find(c => c.title === 'VILLE');
                                            if (villeCol) {
                                              axios.patch(`${API}/clients/${client.row_number}`, {
                                                field_index: villeCol.index,
                                                value: suggestion.ville
                                              }).catch(e => console.error(e));
                                            }
                                          }
                                        }}
                                        className="w-full px-3 py-2 text-left hover:bg-slate-700 border-b border-slate-700 last:border-b-0 transition-colors"
                                      >
                                        <p className="text-sm text-white">{suggestion.full || suggestion.adresse}</p>
                                        <p className="text-xs text-slate-400">{suggestion.cp} {suggestion.ville}</p>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p className="text-white text-sm font-medium truncate" title={value}>
                                {value || <span className="text-slate-600">—</span>}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : activeTab === 'exchanges' ? (
            /* Onglet Échanges (emails rattachés) */
            <div className="p-5">
              {loadingExchanges ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : exchanges.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucun échange rattaché à ce client</p>
                  <p className="text-sm mt-1">Les emails traités par SAN-IA apparaîtront ici</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-slate-400 mb-4">{exchanges.length} échange(s) rattaché(s)</p>
                  {exchanges.map((exchange, idx) => (
                    <div 
                      key={idx} 
                      onClick={() => setSelectedExchangeDetail(exchange)}
                      className="bg-slate-800/50 rounded-lg p-4 border border-slate-700 cursor-pointer hover:border-cyan-500/50 hover:bg-slate-800 transition-all"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          exchange.source_type === 'email' ? 'bg-blue-500/20' :
                          exchange.source_type === 'sms' ? 'bg-green-500/20' : 'bg-orange-500/20'
                        }`}>
                          {exchange.source_type === 'email' ? (
                            <Mail className="w-5 h-5 text-blue-400" />
                          ) : exchange.source_type === 'sms' ? (
                            <MessageSquare className="w-5 h-5 text-green-400" />
                          ) : (
                            <Phone className="w-5 h-5 text-orange-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-white">{exchange.original_data?.subject || '(Sans sujet)'}</p>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              exchange.auto_linked 
                                ? 'bg-purple-500/20 text-purple-400' 
                                : 'bg-amber-500/20 text-amber-400'
                            }`}>
                              {exchange.auto_linked ? '🤖 SAN-IA' : '👤 Manuel'}
                            </span>
                          </div>
                          <p className="text-sm text-slate-400">De: {exchange.original_data?.from || 'Inconnu'}</p>
                          {exchange.summary && (
                            <p className="text-sm text-cyan-400 mt-2 italic">📝 {exchange.summary}</p>
                          )}
                          <div className="flex items-center justify-between mt-2">
                            <p className="text-xs text-slate-500">
                              {exchange.timestamp ? new Date(exchange.timestamp).toLocaleString('fr-FR') : ''}
                            </p>
                            <span className="text-xs text-cyan-400 flex items-center gap-1">
                              <Eye className="w-3 h-3" /> Voir détail
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Modal détail de l'échange */}
              {selectedExchangeDetail && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4" onClick={() => setSelectedExchangeDetail(null)}>
                  <div className="bg-slate-800 rounded-xl w-full max-w-3xl max-h-[85vh] overflow-hidden border border-slate-600" onClick={e => e.stopPropagation()}>
                    <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                      <h3 className="font-semibold text-white">📧 Détail de l'échange</h3>
                      <button onClick={() => setSelectedExchangeDetail(null)} className="p-2 hover:bg-slate-700 rounded-lg">
                        <X className="w-5 h-5 text-slate-400" />
                      </button>
                    </div>
                    <div className="p-5 overflow-y-auto max-h-[calc(85vh-80px)]">
                      <div className="mb-4">
                        <h4 className="text-lg font-medium text-white">{selectedExchangeDetail.original_data?.subject || '(Sans sujet)'}</h4>
                        <p className="text-sm text-slate-400 mt-1">De: {selectedExchangeDetail.original_data?.from || 'Inconnu'}</p>
                        <p className="text-xs text-slate-500 mt-1">{selectedExchangeDetail.timestamp ? new Date(selectedExchangeDetail.timestamp).toLocaleString('fr-FR') : ''}</p>
                      </div>
                      {selectedExchangeDetail.summary && (
                        <div className="mb-4 p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                          <p className="text-xs text-cyan-400 mb-1">🤖 Résumé SAN-IA</p>
                          <p className="text-sm text-cyan-300">{selectedExchangeDetail.summary}</p>
                        </div>
                      )}
                      <div className="bg-slate-900/50 rounded-lg p-4">
                        <p className="text-xs text-slate-500 mb-2">Contenu complet</p>
                        <pre className="text-sm text-slate-300 whitespace-pre-wrap">{selectedExchangeDetail.original_data?.body || selectedExchangeDetail.original_data?.body_text || 'Contenu non disponible'}</pre>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : activeTab === 'history' ? (
            <ClientCommunicationHistory 
              phoneNumber={phoneNumber}
              allPhones={allPhones}
              clientName={clientName}
            />
          ) : activeTab === 'rdv-history' ? (
            /* Onglet Historique RDV */
            <div className="p-5">
              {loadingRdvHistory ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : rdvHistory.length === 0 && rdvSupprimes.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucun RDV enregistré pour ce client</p>
                  <p className="text-sm mt-1">Les RDV R1 et R2 apparaîtront ici</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* RDV actifs */}
                  {rdvHistory.length > 0 && (
                    <>
                      <p className="text-sm text-slate-400">{rdvHistory.length} RDV actif(s)</p>
                      {rdvHistory.map((rdv, idx) => (
                        <div 
                          key={`active-${idx}`} 
                          className={`bg-slate-800/50 rounded-xl p-4 border ${
                            rdv.type === 'R1' ? 'border-green-500/30' : 'border-cyan-500/30'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                rdv.type === 'R1' ? 'bg-green-500/20' : 'bg-cyan-500/20'
                              }`}>
                                <Calendar className={`w-5 h-5 ${
                                  rdv.type === 'R1' ? 'text-green-400' : 'text-cyan-400'
                                }`} />
                              </div>
                              <div>
                                <h4 className="font-medium text-white">{rdv.type_label}</h4>
                                <p className="text-xs text-slate-400">{rdv.type}</p>
                              </div>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              rdv.statut === 'Effectué' 
                                ? 'bg-green-500/20 text-green-400' 
                                : rdv.statut === 'Aujourd\'hui'
                                ? 'bg-amber-500/20 text-amber-400'
                                : 'bg-blue-500/20 text-blue-400'
                            }`}>
                              {rdv.statut}
                            </span>
                          </div>
                          
                          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <p className="text-xs text-slate-500 uppercase">Date RDV</p>
                              <p className="text-sm text-white font-medium">{rdv.date_rdv || '—'}</p>
                            </div>
                            {rdv.heure && (
                              <div>
                                <p className="text-xs text-slate-500 uppercase">Heure</p>
                                <p className="text-sm text-white">{rdv.heure}</p>
                              </div>
                            )}
                            <div>
                              <p className="text-xs text-slate-500 uppercase">{rdv.type === 'R1' ? 'Technicien' : 'Architecte'}</p>
                              <p className="text-sm text-white">{rdv.technicien || '—'}</p>
                            </div>
                            {rdv.date_prise_rdv && (
                              <div>
                                <p className="text-xs text-slate-500 uppercase">Prise RDV</p>
                                <p className="text-sm text-slate-400">{rdv.date_prise_rdv}</p>
                              </div>
                            )}
                          </div>
                          
                          {(rdv.rdv_confirme || rdv.resultat) && (
                            <div className="mt-3 pt-3 border-t border-slate-700 flex items-center gap-4">
                              {rdv.rdv_confirme && (
                                <div className="flex items-center gap-2">
                                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                                  <span className="text-sm text-green-400">Confirmé: {rdv.rdv_confirme}</span>
                                </div>
                              )}
                              {rdv.resultat && (
                                <div>
                                  <span className="text-xs text-slate-500">Résultat: </span>
                                  <span className="text-sm text-slate-300">{rdv.resultat}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                  
                  {/* RDV supprimés */}
                  {rdvSupprimes.length > 0 && (
                    <>
                      <div className="mt-6 pt-4 border-t border-slate-700">
                        <p className="text-sm text-red-400 mb-4 flex items-center gap-2">
                          <Trash2 className="w-4 h-4" />
                          {rdvSupprimes.length} RDV supprimé(s)
                        </p>
                      </div>
                      {rdvSupprimes.map((rdv, idx) => (
                        <div 
                          key={`deleted-${idx}`} 
                          className="bg-slate-800/30 rounded-xl p-4 border border-red-500/20 opacity-75"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-red-500/10">
                                <Trash2 className="w-5 h-5 text-red-400" />
                              </div>
                              <div>
                                <h4 className="font-medium text-slate-300">{rdv.type_label}</h4>
                                <p className="text-xs text-slate-500">{rdv.type}</p>
                              </div>
                            </div>
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                              Supprimé
                            </span>
                          </div>
                          
                          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                            <div>
                              <p className="text-xs text-slate-500 uppercase">Date RDV prévue</p>
                              <p className="text-sm text-slate-400 line-through">{rdv.date_rdv || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 uppercase">Raison</p>
                              <p className="text-sm text-red-400">{rdv.raison_label || rdv.raison}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 uppercase">Supprimé par</p>
                              <p className="text-sm text-slate-400">{rdv.supprime_par || '—'}</p>
                            </div>
                          </div>
                          
                          {rdv.commentaire && (
                            <div className="mt-3 pt-3 border-t border-slate-700/50">
                              <p className="text-xs text-slate-500 uppercase mb-1">Commentaire</p>
                              <p className="text-sm text-slate-400 italic">"{rdv.commentaire}"</p>
                            </div>
                          )}
                          
                          {rdv.date_suppression && (
                            <div className="mt-2 text-xs text-slate-600">
                              Supprimé le {new Date(rdv.date_suppression).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          ) : activeTab === 'rdv' ? (
            /* Onglet Prendre RDV */
            <div className="p-5">
              <div className="max-w-2xl mx-auto">
                <div className="bg-gradient-to-br from-emerald-900/30 to-slate-900 border border-emerald-500/30 rounded-xl p-6 mb-6">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
                    <CalendarPlus className="w-5 h-5 text-emerald-400" />
                    Compléter les informations pour prendre un RDV
                  </h3>
                  <p className="text-sm text-slate-400 mb-6">
                    Remplissez tous les champs obligatoires (*) pour pouvoir planifier un RDV avec ce client.
                  </p>
                  
                  <div className="space-y-4">
                    {/* Nom et Prénom */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Nom *</label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                          <input
                            type="text"
                            value={rdvFormData.nom}
                            onChange={e => setRdvFormData(prev => ({ ...prev, nom: e.target.value }))}
                            className="w-full pl-10 pr-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder:text-slate-500 focus:border-emerald-500 focus:outline-none"
                            placeholder="Nom du client"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Prénom *</label>
                        <input
                          type="text"
                          value={rdvFormData.prenom}
                          onChange={e => setRdvFormData(prev => ({ ...prev, prenom: e.target.value }))}
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder:text-slate-500 focus:border-emerald-500 focus:outline-none"
                          placeholder="Prénom du client"
                        />
                      </div>
                    </div>
                    
                    {/* Téléphone */}
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Téléphone *</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <input
                          type="tel"
                          value={rdvFormData.telephone}
                          onChange={e => setRdvFormData(prev => ({ ...prev, telephone: e.target.value }))}
                          className="w-full pl-10 pr-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder:text-slate-500 focus:border-emerald-500 focus:outline-none"
                          placeholder="06 12 34 56 78"
                        />
                      </div>
                    </div>
                    
                    {/* Adresse avec autocomplétion */}
                    <div className="relative">
                      <label className="block text-xs text-slate-400 mb-1">Adresse * (commencez à taper)</label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <input
                          type="text"
                          value={rdvFormData.adresse}
                          onChange={e => {
                            setRdvFormData(prev => ({ ...prev, adresse: e.target.value }));
                            searchAddress(e.target.value);
                          }}
                          onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                          className="w-full pl-10 pr-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder:text-slate-500 focus:border-emerald-500 focus:outline-none"
                          placeholder="Ex: 12 rue de la Paix, Paris"
                        />
                        {loadingAddress && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
                          </div>
                        )}
                      </div>
                      
                      {/* Suggestions d'adresses */}
                      {showSuggestions && addressSuggestions.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                          {addressSuggestions.map((suggestion, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => selectAddress(suggestion)}
                              className="w-full px-4 py-3 text-left hover:bg-slate-700 border-b border-slate-700 last:border-b-0 transition-colors"
                            >
                              <p className="text-sm text-white">{suggestion.full || suggestion.adresse}</p>
                              <p className="text-xs text-slate-400">{suggestion.cp} {suggestion.ville}</p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* CP et Ville */}
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Code Postal</label>
                        <input
                          type="text"
                          value={rdvFormData.cp}
                          onChange={e => setRdvFormData(prev => ({ ...prev, cp: e.target.value }))}
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder:text-slate-500 focus:border-emerald-500 focus:outline-none"
                          placeholder="75001"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs text-slate-400 mb-1">Ville *</label>
                        <input
                          type="text"
                          value={rdvFormData.ville}
                          onChange={e => setRdvFormData(prev => ({ ...prev, ville: e.target.value }))}
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder:text-slate-500 focus:border-emerald-500 focus:outline-none"
                          placeholder="Paris"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Résumé et boutons */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Champs remplis</p>
                      <div className="flex items-center gap-2 mt-1">
                        {[
                          { field: 'nom', label: 'Nom' },
                          { field: 'prenom', label: 'Prénom' },
                          { field: 'telephone', label: 'Tél' },
                          { field: 'adresse', label: 'Adresse' },
                          { field: 'ville', label: 'Ville' }
                        ].map(({ field, label }) => (
                          <span 
                            key={field}
                            className={`px-2 py-0.5 rounded text-xs ${
                              rdvFormData[field]?.trim() 
                                ? 'bg-emerald-500/20 text-emerald-400' 
                                : 'bg-red-500/20 text-red-400'
                            }`}
                          >
                            {rdvFormData[field]?.trim() ? '✓' : '✗'} {label}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* Bouton Planifier le RDV */}
                      <button
                        onClick={handlePrendreRdv}
                        disabled={!isRdvFormComplete() || saving}
                        className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-all ${
                          isRdvFormComplete() && !saving
                            ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                            : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                        }`}
                        data-testid="planifier-rdv-btn"
                      >
                        {saving ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <CalendarPlus className="w-4 h-4" />
                        )}
                        {saving ? 'Enregistrement...' : 'Planifier le RDV'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : activeTab === 'factures' ? (
            /* Onglet Factures Fournisseurs */
            <div className="p-5">
              {loadingFactures ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : factures.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <p>Aucune facture rattachée à ce client</p>
                </div>
              ) : (
                <>
                  {/* Totaux */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                      <p className="text-xs text-slate-400 mb-1">Nombre de factures</p>
                      <p className="text-2xl font-bold text-white">{facturesTotaux.count}</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                      <p className="text-xs text-slate-400 mb-1">Total HT</p>
                      <p className="text-2xl font-bold text-blue-400">{facturesTotaux.total_ht.toLocaleString('fr-FR')} €</p>
                    </div>
                    <div className="bg-amber-500/10 rounded-lg p-4 border border-amber-500/30">
                      <p className="text-xs text-amber-400 mb-1">Total TTC (Matériaux)</p>
                      <p className="text-2xl font-bold text-amber-400">{facturesTotaux.total_ttc.toLocaleString('fr-FR')} €</p>
                    </div>
                  </div>
                  
                  {/* Liste des factures */}
                  <div className="space-y-2">
                    {factures.map(facture => (
                      <div 
                        key={facture._id} 
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          facture.document_type === 'avoir' 
                            ? 'bg-green-500/10 border-green-500/30' 
                            : 'bg-slate-800/50 border-slate-700'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{facture.document_type === 'avoir' ? '📗' : '🧾'}</span>
                          <div>
                            <p className="text-white font-medium">
                              {facture.fournisseur || 'Fournisseur inconnu'}
                              {facture.numero_facture && <span className="text-slate-400 ml-2">#{facture.numero_facture}</span>}
                            </p>
                            <p className="text-xs text-slate-400">
                              {facture.date_facture || 'Date inconnue'}
                              {facture.est_reglee && <span className="text-green-400 ml-2">✓ Réglée</span>}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-bold">{(facture.montant_ttc || 0).toLocaleString('fr-FR')} € TTC</p>
                          <p className="text-xs text-slate-400">{(facture.montant_ht || 0).toLocaleString('fr-FR')} € HT</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : activeTab === 'encaissements' ? (
            /* Onglet Encaissements */
            <div className="p-5">
              {loadingEncaissements ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : clientEncaissements.encaissements.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <p>Aucun encaissement rattaché à ce client</p>
                  <p className="text-sm mt-2">Les encaissements sont créés depuis Compta / Finances → Rattacher à un client</p>
                </div>
              ) : (
                <>
                  {/* Total des encaissements */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                      <p className="text-xs text-slate-400 mb-1">Nombre d'encaissements</p>
                      <p className="text-2xl font-bold text-white">{clientEncaissements.count}</p>
                    </div>
                    <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/30">
                      <p className="text-xs text-green-400 mb-1">Total encaissé</p>
                      <p className="text-2xl font-bold text-green-400">+{clientEncaissements.total.toLocaleString('fr-FR')} €</p>
                    </div>
                  </div>
                  
                  {/* Liste des encaissements */}
                  <div className="space-y-2">
                    {clientEncaissements.encaissements.map((enc, idx) => (
                      <div 
                        key={enc.id || idx}
                        className="flex items-center justify-between p-3 rounded-lg bg-green-500/5 border border-green-500/20"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">💰</span>
                          <div>
                            <p className="text-white font-medium">
                              {enc.payment_condition_label || 'Encaissement'}
                            </p>
                            <p className="text-xs text-slate-400">
                              {enc.date ? new Date(enc.date).toLocaleDateString('fr-FR') : 'Date inconnue'}
                              {enc.label && <span className="ml-2 text-slate-500">• {enc.label.substring(0, 30)}...</span>}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-green-400 font-bold text-lg">+{(enc.amount || 0).toLocaleString('fr-FR')} €</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : activeTab === 'rappels' ? (
            /* Onglet Rappels */
            <div className="p-5">
              {loadingRappels ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : clientRappels.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucun rappel pour ce client</p>
                  <p className="text-sm mt-2">Les rappels peuvent être créés depuis l'espace Télépro ou le calendrier</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {clientRappels.map((rappel, idx) => (
                    <div 
                      key={rappel.id || idx}
                      className={`flex items-start gap-3 p-4 rounded-lg border transition-colors ${
                        rappel.fait 
                          ? 'bg-slate-800/30 border-slate-700 opacity-60' 
                          : 'bg-amber-500/5 border-amber-500/30 hover:border-amber-500/50'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        rappel.fait ? 'bg-slate-700' : 'bg-amber-500/20'
                      }`}>
                        {rappel.fait ? (
                          <Check className="w-5 h-5 text-slate-400" />
                        ) : (
                          <Bell className="w-5 h-5 text-amber-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-medium ${rappel.fait ? 'text-slate-400 line-through' : 'text-white'}`}>
                            {rappel.date ? new Date(rappel.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) : 'Date non définie'}
                          </span>
                          {rappel.heure && (
                            <span className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded">
                              {rappel.heure}
                            </span>
                          )}
                          {rappel.fait && (
                            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">
                              Fait
                            </span>
                          )}
                        </div>
                        {rappel.motif && (
                          <p className={`text-sm ${rappel.fait ? 'text-slate-500' : 'text-slate-300'}`}>
                            {rappel.motif}
                          </p>
                        )}
                        {rappel.user && (
                          <p className="text-xs text-slate-500 mt-1">
                            Par: {rappel.user}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : activeTab === 'historique' ? (
            /* Onglet Historique Timeline */
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Activity className="w-5 h-5 text-purple-400" />
                  Timeline des activités
                </h3>
                {activities.length > 0 && (
                  <span className="text-sm text-slate-400">
                    {activities.length} événement{activities.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              
              {loadingActivities ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : activities.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucune activité enregistrée</p>
                  <p className="text-sm mt-2">L'historique des actions sur ce dossier apparaîtra ici</p>
                </div>
              ) : (
                <div className="relative">
                  {/* Ligne verticale de la timeline */}
                  <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-700" />
                  
                  <div className="space-y-4">
                    {activities.map((activity, idx) => {
                      // Définir l'icône et la couleur selon le type d'activité
                      const getActivityStyle = (type) => {
                        switch (type) {
                          case 'status_change':
                            return { icon: <RefreshCw className="w-4 h-4" />, color: 'bg-blue-500', borderColor: 'border-blue-500' };
                          case 'call':
                            return { icon: <PhoneCall className="w-4 h-4" />, color: 'bg-emerald-500', borderColor: 'border-emerald-500' };
                          case 'rdv_booked':
                            return { icon: <CalendarPlus className="w-4 h-4" />, color: 'bg-purple-500', borderColor: 'border-purple-500' };
                          case 'rdv_done':
                            return { icon: <CalendarCheck className="w-4 h-4" />, color: 'bg-green-500', borderColor: 'border-green-500' };
                          case 'rdv_cancelled':
                            return { icon: <X className="w-4 h-4" />, color: 'bg-red-500', borderColor: 'border-red-500' };
                          case 'note':
                            return { icon: <FileEdit className="w-4 h-4" />, color: 'bg-amber-500', borderColor: 'border-amber-500' };
                          case 'reminder':
                            return { icon: <Bell className="w-4 h-4" />, color: 'bg-orange-500', borderColor: 'border-orange-500' };
                          case 'email':
                            return { icon: <Mail className="w-4 h-4" />, color: 'bg-cyan-500', borderColor: 'border-cyan-500' };
                          case 'sms':
                            return { icon: <MessageSquare className="w-4 h-4" />, color: 'bg-pink-500', borderColor: 'border-pink-500' };
                          case 'lead_created':
                            return { icon: <Plus className="w-4 h-4" />, color: 'bg-indigo-500', borderColor: 'border-indigo-500' };
                          default:
                            return { icon: <Activity className="w-4 h-4" />, color: 'bg-slate-500', borderColor: 'border-slate-500' };
                        }
                      };
                      
                      const style = getActivityStyle(activity.type);
                      const timestamp = activity.timestamp ? new Date(activity.timestamp) : null;
                      
                      return (
                        <div key={idx} className="relative flex gap-4 pl-2">
                          {/* Point sur la timeline */}
                          <div className={`relative z-10 w-10 h-10 rounded-full ${style.color} flex items-center justify-center text-white shadow-lg`}>
                            {style.icon}
                          </div>
                          
                          {/* Contenu de l'activité */}
                          <div className={`flex-1 bg-slate-800/50 border ${style.borderColor}/30 rounded-lg p-4 hover:border-opacity-50 transition-colors`}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <p className="text-white font-medium">{activity.description}</p>
                              {timestamp && (
                                <span className="text-xs text-slate-500 whitespace-nowrap">
                                  {timestamp.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                                  {' à '}
                                  {timestamp.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              )}
                            </div>
                            
                            {/* Métadonnées */}
                            {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {activity.metadata.old_stage_name && activity.metadata.new_stage_name && (
                                  <span className="text-xs bg-slate-700 text-slate-300 px-2 py-1 rounded flex items-center gap-1">
                                    <span style={{ color: activity.metadata.old_color || '#94a3b8' }}>{activity.metadata.old_stage_name}</span>
                                    <ChevronRight className="w-3 h-3 text-slate-500" />
                                    <span style={{ color: activity.metadata.new_color || '#94a3b8' }}>{activity.metadata.new_stage_name}</span>
                                  </span>
                                )}
                                {activity.metadata.note && (
                                  <span className="text-xs text-slate-400 italic">
                                    "{activity.metadata.note}"
                                  </span>
                                )}
                              </div>
                            )}
                            
                            {/* Utilisateur */}
                            {activity.user && (
                              <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {activity.user}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : activeTab === 'tasks_internes' ? (
            /* Onglet Taches Internes */
            <div className="p-5">
              <TasksPanel
                authUser={authUser}
                clientId={client?.row_number?.toString()}
                clientName={clientName}
                compact={true}
              />
            </div>
          ) : activeTab === 'eligibilites' ? (
            /* Onglet Eligibilites & TS */
            <EligibilitesTab
              clientId={client?.row_number}
              onNewEligibilite={() => {
                if (onResumeQualification) {
                  onResumeQualification({
                    row_number: client?.row_number,
                    nom: client?.nom || client?.data?.['CLIENT NOM'],
                    prenom: client?.prenom || client?.data?.['CLIENT PRÉNOM'],
                    telephone: client?.telephone || client?.data?.['CLIENT TELEPHONE'] || client?.data?.['CLIENT TELEPHONE 2'],
                    email: client?.email || client?.data?.['CLIENT EMAIL'] || '',
                    adresse: client?.adresse || client?.data?.['ADRESSE RDV'] || '',
                    code_postal: client?.code_postal || client?.data?.['CODE POSTAL RDV'] || '',
                    ville: client?.ville || client?.data?.['VILLE RDV'] || '',
                  });
                }
              }}
            />
          ) : activeTab === 'devis' ? (
            /* Onglet Devis */
            <div className="p-5">
              {/* Sous-onglets Devis */}
              <div className="flex gap-2 mb-6">
                {[
                  { key: 'mpa', label: 'MPA', color: 'violet' },
                  { key: 'pac_iso', label: 'PAC / ISO', color: 'orange' },
                  { key: 'tc', label: 'TC', color: 'teal' }
                ].map(st => (
                  <button
                    key={st.key}
                    data-testid={`devis-subtab-${st.key}`}
                    onClick={() => setDevisSousTab(st.key)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      devisSousTab === st.key
                        ? st.color === 'violet' ? 'bg-violet-500/20 text-violet-400 border border-violet-500/50'
                          : st.color === 'orange' ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                          : 'bg-teal-500/20 text-teal-400 border border-teal-500/50'
                        : 'bg-slate-800 text-slate-400 border border-slate-700 hover:text-white hover:bg-slate-700'
                    }`}
                  >
                    {st.label}
                  </button>
                ))}
              </div>

              {/* Contenu sous-onglet MPA */}
              {devisSousTab === 'mpa' && (
                <div data-testid="devis-mpa-content" className="space-y-4">
                  {loadingDevis ? (
                    <div className="text-center py-8 text-slate-400">Chargement des devis...</div>
                  ) : clientDevis.filter(d => d.type_devis === 'mpa').length === 0 ? (
                    <div className="text-center py-8 text-slate-500">Aucun devis MPA pour ce client</div>
                  ) : (
                    clientDevis.filter(d => d.type_devis === 'mpa').map(devis => (
                      <div key={devis.id} className="bg-slate-800/50 border border-violet-500/30 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-bold text-violet-400">{devis.numero}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              devis.statut === 'brouillon' ? 'bg-slate-700 text-slate-300' :
                              devis.statut === 'envoye' ? 'bg-blue-500/20 text-blue-400' :
                              devis.statut === 'accepte' ? 'bg-emerald-500/20 text-emerald-400' :
                              'bg-red-500/20 text-red-400'
                            }`}>{devis.statut}</span>
                          </div>
                          <span className="text-xs text-slate-500">{new Date(devis.created_at).toLocaleDateString('fr-FR')}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-sm">
                          <div>
                            <span className="text-xs text-slate-500">Total HT</span>
                            <div className="text-white font-mono font-bold">{Number(devis.total_ht || 0).toLocaleString('fr-FR', {minimumFractionDigits: 2})} €</div>
                          </div>
                          <div>
                            <span className="text-xs text-slate-500">Total TTC</span>
                            <div className="text-white font-mono">{Number(devis.total_ttc || 0).toLocaleString('fr-FR', {minimumFractionDigits: 2})} €</div>
                          </div>
                          <div>
                            <span className="text-xs text-slate-500">Lignes</span>
                            <div className="text-white">{(devis.lignes || []).length} prestations</div>
                          </div>
                        </div>
                        {devis.lignes && devis.lignes.length > 0 && (
                          <details className="mt-3">
                            <summary className="text-xs text-slate-400 cursor-pointer hover:text-white">Voir le detail ({devis.lignes.length} lignes)</summary>
                            <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                              {devis.lignes.map((l, i) => (
                                <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-slate-700/30">
                                  <span className="text-slate-300 truncate flex-1">{l.nom}</span>
                                  <span className="text-slate-400 mx-2">{l.quantite} x</span>
                                  <span className="text-white font-mono w-20 text-right">{Number(l.total_ht || 0).toLocaleString('fr-FR', {minimumFractionDigits: 2})} €</span>
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Contenu sous-onglet PAC/ISO */}
              {devisSousTab === 'pac_iso' && (
                <div data-testid="devis-pac-iso-content" className="bg-gradient-to-br from-orange-900/20 to-amber-900/20 border border-orange-500/30 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-orange-400 mb-2">Devis PAC / ISO</h3>
                  <p className="text-sm text-slate-400">Pompe a chaleur & Isolation - Devis et estimations</p>
                </div>
              )}

              {/* Contenu sous-onglet TC */}
              {devisSousTab === 'tc' && (
                <div data-testid="devis-tc-content" className="bg-gradient-to-br from-teal-900/20 to-cyan-900/20 border border-teal-500/30 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-teal-400 mb-2">Devis TC</h3>
                  <p className="text-sm text-slate-400">Travaux de Confort - Devis et estimations</p>
                </div>
              )}
            </div>
          ) : activeTab === 'whatsapp_exchanges' ? (
            <WhatsAppExchangesTab clientId={client?.client_id} />
          ) : null}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════════
function App({ authUser }) {
  const navigate = useNavigate();
  const [poles, setPoles] = useState([]);
  const [currentPole, setCurrentPole] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [clients, setClients] = useState([]);
  const [columns, setColumns] = useState([]);
  const [dropdowns, setDropdowns] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [search, setSearch] = useState("");
  const [alertFilter, setAlertFilter] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showSearchPage, setShowSearchPage] = useState(false);
  const [showChristelleSearch, setShowChristelleSearch] = useState(false);
  const [showStatsPage, setShowStatsPage] = useState(false);
  const [showTeleproDashboard, setShowTeleproDashboard] = useState(false);
  const [showUsersPage, setShowUsersPage] = useState(false);
  const [showSalleReunion, setShowSalleReunion] = useState(false);
  const [showMarketing, setShowMarketing] = useState(false);
  const [showCommunicationsHub, setShowCommunicationsHub] = useState(false);
  const [showAdministratif, setShowAdministratif] = useState(false);
  const [showChantiers, setShowChantiers] = useState(false);
  const [showAidesComp, setShowAidesComp] = useState(false);
  const [showQualificationAppel, setShowQualificationAppel] = useState(false);
  const [resumeQualClient, setResumeQualClient] = useState(null);
  const [showDevisTypeModal, setShowDevisTypeModal] = useState(false);
  const [devisCreationType, setDevisCreationType] = useState(null);

  // Handler pour ouvrir la qualification appel
  const handleOpenQualificationAppel = () => {
    setResumeQualClient(null);
    setShowQualificationAppel(true);
  };

  // Handler pour reprendre une qualification en cours
  const handleResumeQualification = (client) => {
    setSelectedClient(null); // Close client modal first
    setResumeQualClient(client);
    setShowQualificationAppel(true);
  };

  // Logout handler
  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      await authFetch(`${getApiUrl()}/api/auth/logout`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch (e) {
      console.error('Logout error:', e);
    }
    // Clear localStorage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    navigate('/login', { replace: true });
  };

  // Fetch poles on mount
  useEffect(() => {
    const fetchPoles = async () => {
      try {
        const [polesRes, dropdownsRes] = await Promise.all([
          axios.get(`${API}/poles`),
          axios.get(`${API}/dropdowns`)
        ]);
        setPoles(polesRes.data);
        setDropdowns(dropdownsRes.data);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    fetchPoles();
  }, []);

  // Fetch pole data when pole changes
  const fetchPoleData = useCallback(async () => {
    if (!currentPole) return;
    setLoading(true);
    try {
      const [alertsRes, clientsRes, statsRes] = await Promise.all([
        axios.get(`${API}/poles/${currentPole.id}/alerts`),
        axios.get(`${API}/poles/${currentPole.id}/clients`, {
          params: { search: search || undefined, alert_filter: alertFilter || undefined }
        }),
        axios.get(`${API}/stats/${currentPole.id}`)
      ]);
      setAlerts(alertsRes.data);
      setClients(clientsRes.data.clients);
      setColumns(clientsRes.data.columns);
      setStats(statsRes.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [currentPole, search, alertFilter]);

  useEffect(() => {
    if (currentPole) {
      fetchPoleData();
    }
  }, [currentPole, fetchPoleData]);

  const handleClientClick = async (rowNumber) => {
    try {
      const res = await axios.get(`${API}/clients/${rowNumber}`, {
        params: { pole_id: currentPole.id }
      });
      setSelectedClient(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAlertClick = (alert) => {
    if (alertFilter === alert.status) {
      setAlertFilter(null);
    } else {
      setAlertFilter(alert.status);
    }
  };

  const handleBackToMenu = () => {
    setCurrentPole(null);
    setAlerts([]);
    setClients([]);
    setColumns([]);
    setStats(null);
    setAlertFilter(null);
    setSearch("");
  };

  // Reset data when pole changes
  useEffect(() => {
    if (currentPole) {
      setAlerts([]);
      setClients([]);
      setColumns([]);
      setAlertFilter(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPole?.id]);

  // Handle client click from search results (full Super Admin view)
  const handleSearchClientClick = async (rowNumber, clientId) => {
    try {
      // Si on a un client_id, charger depuis MongoDB pipeline d'abord (plus fiable que row_number)
      if (clientId) {
        try {
          const pipelineRes = await axios.get(`${API}/pipeline/client/${encodeURIComponent(clientId)}`);
          if (pipelineRes.data) {
            const actualRow = pipelineRes.data.row_number || rowNumber;
            if (actualRow) {
              const res = await axios.get(`${API}/clients/${actualRow}`, {
                params: { pole_id: 'Super Admin', client_id: clientId }
              });
              if (res.data) {
                // Merger le nom/prenom depuis pipeline si le sheet ne correspond pas
                const sheetNom = (res.data.data?.['CLIENT NOM'] || '').trim().toUpperCase();
                const mongoNom = (pipelineRes.data.nom || '').trim().toUpperCase();
                if (mongoNom && sheetNom && sheetNom !== mongoNom) {
                  res.data.data['CLIENT NOM'] = pipelineRes.data.nom || '';
                  res.data.data['PRÉNOM'] = pipelineRes.data.prenom || '';
                  if (pipelineRes.data.tel_client || pipelineRes.data.telephone)
                    res.data.data['TEL CLIENT'] = pipelineRes.data.tel_client || pipelineRes.data.telephone || '';
                  if (pipelineRes.data.email)
                    res.data.data['EMAIL'] = pipelineRes.data.email;
                }
                // Passer le client_id pour que le useEffect by-row utilise le bon document MongoDB
                res.data._clientId = clientId;
                setSelectedClient(res.data);
                return;
              }
            }
            // Pas de row_number valide, construire une fiche depuis les données pipeline
            setSelectedClient({
              row_number: rowNumber,
              _clientId: clientId,
              columns: [],
              data: {
                'CLIENT NOM': pipelineRes.data.nom || '',
                'PRÉNOM': pipelineRes.data.prenom || '',
                'TEL CLIENT': pipelineRes.data.tel_client || pipelineRes.data.telephone || '',
                'EMAIL': pipelineRes.data.email || '',
                'DPT': pipelineRes.data.dpt || '',
              },
              ...pipelineRes.data
            });
            return;
          }
        } catch (pipeErr) {
          console.log('Pipeline load failed, falling back to sheet:', pipeErr.message);
        }
      }
      // Fallback: charger depuis MongoDB par identifier
      if (rowNumber || clientId) {
        const identifier = clientId || rowNumber;
        const res = await axios.get(`${API}/clients/${encodeURIComponent(identifier)}`, {
          params: { pole_id: 'Super Admin' }
        });
        res.data._clientId = clientId;
        setSelectedClient(res.data);
      }
    } catch (err) {
      console.error(err);
    }
  };
  
  // Handler spécifique pour voir un client depuis le calendrier
  const handleViewClientFromCalendar = async (rowNumber) => {
    try {
      const res = await axios.get(`${API}/clients/${encodeURIComponent(rowNumber)}`, {
        params: { pole_id: 'Super Admin' }
      });
      setShowCalendrier(false);
      setSelectedClient(res.data);
    } catch (err) {
      console.error("Error in handleViewClientFromCalendar:", err);
    }
  };
  
  // Handler pour prendre un RDV depuis le modal client
  const handlePrendreRdvFromModal = (rowNumber, formData) => {
    console.log("handlePrendreRdvFromModal called with row:", rowNumber, formData);
    // Fermer le modal client et ouvrir le calendrier pour planifier le RDV
    setSelectedClient(null);
    setShowTeleproDashboard(false);
    setShowSearchPage(false);
    // Stocker les données du client pour le calendrier avec l'intervenant
    window.sessionStorage.setItem('pendingRdvClient', JSON.stringify({ 
      rowNumber, 
      ...formData,
      intervenant: formData.intervenant || '' 
    }));
    setShowCalendrier(true);
  };

  // State for admin page
  const [showAdminPage, setShowAdminPage] = useState(false);
  
  // State for parcours page
  const [showParcoursPage, setShowParcoursPage] = useState(false);
  
  // State for factures page
  const [showFacturesPage, setShowFacturesPage] = useState(false);
  const [showRapprochementPage, setShowRapprochementPage] = useState(false);
  
  // State for Super Batard dashboard
  const [showSuperBatard, setShowSuperBatard] = useState(false);
  const [showConsoleAdmin, setShowConsoleAdmin] = useState(false);
  
  // State for Super Batard Compta v2 dashboard
  const [showSuperBatardCompta, setShowSuperBatardCompta] = useState(false);
  
  // State for Consolidation Comptable v2
  const [showConsolidationV2, setShowConsolidationV2] = useState(false);
  
  // State for Compta/Finances dashboard
  const [showComptaFinances, setShowComptaFinances] = useState(false);
  
  // State for Compta / Finances dashboard
  const [showComptaFiNew, setShowComptaFiNew] = useState(false);
  
  // State for Grands Comptes dashboard
  const [showGrandsComptes, setShowGrandsComptes] = useState(false);
  
  // State for Calendrier dashboard
  const [showCalendrier, setShowCalendrier] = useState(false);
  
  // State for Mon Espace (espace personnel)
  const [showMonEspace, setShowMonEspace] = useState(false);
  const [monEspaceTab, setMonEspaceTab] = useState(null);

  // Return-to tracking: when navigating away from a dashboard (e.g. to Calendar/Mon Espace),
  // remember which dashboard to return to on "back"
  const [returnToDashboard, setReturnToDashboard] = useState(null);
  
  // State for Comité de Direction dashboard
  const [showComiteDirection, setShowComiteDirection] = useState(false);
  
  // State for Formations dashboard
  const [showFormations, setShowFormations] = useState(false);
  
  // State for Simulateur Aides dashboard
  const [showSimulateurAides, setShowSimulateurAides] = useState(false);
  
  // State for Tests dashboard
  const [showTestsDashboard, setShowTestsDashboard] = useState(false);
  
  // State for Yael Console deep-link
  const [superBatardInitialSection, setSuperBatardInitialSection] = useState(null);

  // Listen for navigate-to-yael-console events (from PreDeploy panel)
  useEffect(() => {
    const handler = () => {
      setShowTestsDashboard(false);
      setSuperBatardInitialSection("yael-console");
      setShowSuperBatard(true);
    };
    window.addEventListener("navigate-to-yael-console", handler);
    return () => window.removeEventListener("navigate-to-yael-console", handler);
  }, []);

  // State for analyse dossiers bloqués page
  const [showAnalyseBloques, setShowAnalyseBloques] = useState(false);
  const [showStatistiquesParcours, setShowStatistiquesParcours] = useState(false);

  // ========== MODAL QUALIFICATION APPEL - DOIT ÊTRE AVANT LES RETURNS ==========
  // Ce composant est rendu en overlay, donc il doit être accessible depuis n'importe quelle vue
  
  // Fonction pour ouvrir la fiche d'un client existant depuis la qualification
  const handleOpenClientFromQualification = async (rowNumber, clientData = null) => {
    try {
      console.log("🔵 handleOpenClientFromQualification called with row:", rowNumber, "clientData:", clientData ? "provided" : "null");
      // Fermer le modal de qualification
      setShowQualificationAppel(false);
      
      // Si clientData fourni (nouveau lead), attendre que Google Sheet soit synchro puis recharger
      if (clientData) {
        // Afficher un toast de chargement
        toast.info('Chargement de la fiche client...', { duration: 2000 });
        
        // Attendre 2 secondes pour laisser Google Sheet se synchroniser
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      
      // Charger le client depuis l'API
      const res = await axios.get(`${API}/clients/${rowNumber}`, {
        params: { pole_id: 'Super Admin' }
      });
      console.log("API response received:", res.data ? "success" : "empty");
      
      // Si les données ont été fournies par la qualification, les merger avec les données chargées
      if (clientData && res.data) {
        // Stocker les infos de qualification dans sessionStorage pour les afficher dans la fiche
        window.sessionStorage.setItem('qualificationData', JSON.stringify({
          simulation_mpa: clientData.simulation_mpa,
          produits_interesses: clientData.produits_interesses,
          statut_qualification: clientData.statut_qualification,
          protection_juridique: clientData.protection_juridique || 'non'
        }));
      }
      
      setSelectedClient(res.data);
    } catch (error) {
      console.error('Erreur ouverture fiche client:', error);
      toast.error('Erreur lors de l\'ouverture de la fiche client');
    }
  };
  
  const qualificationModal = (
    <>
      {showQualificationAppel && (
        <QualificationAppel
          isOpen={true}
          onClose={() => { setShowQualificationAppel(false); setResumeQualClient(null); }}
          authUser={authUser}
          resumeClient={resumeQualClient}
          onLeadCreated={(lead) => {
            console.log('Lead créé:', lead);
          }}
          onOpenClientFiche={handleOpenClientFromQualification}
        />
      )}
    </>
  );

  // ── Navigation centralisée entre dashboards ──
  const closeAllDashboards = () => {
    setShowTeleproDashboard(false);
    setShowStatsPage(false);
    setShowChristelleSearch(false);
    setShowMarketing(false);
    setShowCommunicationsHub(false);
    setShowAdministratif(false);
    setShowChantiers(false);
    setShowAidesComp(false);
    setShowSalleReunion(false);
    setShowAdminPage(false);
    setShowParcoursPage(false);
    setShowFacturesPage(false);
    setShowSuperBatard(false);
    setShowConsoleAdmin(false);
    setShowSuperBatardCompta(false);
    setShowConsolidationV2(false);
    setShowComptaFinances(false);
    setShowComptaFiNew(false);
    setShowCalendrier(false);
    setShowMonEspace(false);
    setShowFormations(false);
    setShowTestsDashboard(false);
    setShowSimulateurAides(false);
    setShowComiteDirection(false);
    setShowGrandsComptes(false);
    setShowRapprochementPage(false);
    setShowStatistiquesParcours(false);
    setShowAnalyseBloques(false);
    setShowUsersPage(false);
    setCurrentPole(null);
  };

  const navigateToDashboard = (dashboardId) => {
    closeAllDashboards();
    if (dashboardId === "home") return;
    const navMap = {
      telepro: () => setShowTeleproDashboard(true),
      r1: () => { const p = poles.find(pl => pl.name === 'R1'); if (p) setCurrentPole(p); },
      r2: () => { const p = poles.find(pl => pl.name === 'R2'); if (p) setCurrentPole(p); },
      amo: () => { const p = poles.find(pl => pl.name === 'AMO'); if (p) setCurrentPole(p); },
      aides_comp: () => setShowAidesComp(true),
      tc_pac_iso: () => { const p = poles.find(pl => pl.name === 'TC/PAC ISO'); if (p) setCurrentPole(p); },
      chantiers: () => setShowChantiers(true),
      calendrier: () => setShowCalendrier(true),
      communications_ia: () => setShowCommunicationsHub(true),
      marketing: () => setShowMarketing(true),
      administratif: () => setShowAdministratif(true),
      grands_comptes: () => setShowGrandsComptes(true),
      formations: () => setShowFormations(true),
      comite_direction: () => setShowComiteDirection(true),
      compta_finances: () => setShowComptaFinances(true),
      compta_fi_new: () => setShowComptaFiNew(true),
      super_batard: () => setShowSuperBatard(true),
      console_admin: () => setShowConsoleAdmin(true),
      tests: () => setShowTestsDashboard(true),
      simulateur_aides: () => setShowSimulateurAides(true),
    };
    const handler = navMap[dashboardId];
    if (handler) handler();
  };

  const getActiveDashboardId = () => {
    if (showTeleproDashboard) return "telepro";
    if (showCalendrier) return "calendrier";
    if (showCommunicationsHub) return "communications_ia";
    if (showMarketing) return "marketing";
    if (showAdministratif) return "administratif";
    if (showChantiers) return "chantiers";
    if (showGrandsComptes) return "grands_comptes";
    if (showFormations) return "formations";
    if (showComiteDirection) return "comite_direction";
    if (showComptaFinances) return "compta_finances";
    if (showComptaFiNew) return "compta_fi_new";
    if (showSuperBatard) return "super_batard";
    if (showConsoleAdmin) return "console_admin";
    if (showTestsDashboard) return "tests";
    if (showSimulateurAides) return "simulateur_aides";
    if (showMonEspace) return "mon_espace";
    if (showAidesComp) return "aides_comp";
    if (currentPole?.name === 'R1') return "r1";
    if (currentPole?.name === 'R2') return "r2";
    if (currentPole?.name === 'AMO') return "amo";
    if (currentPole?.name === 'TC/PAC ISO') return "tc_pac_iso";
    return null;
  };

  const dashboardNavBar = (
    <DashboardNav
      current={getActiveDashboardId()}
      onNavigate={navigateToDashboard}
      authUser={authUser}
    />
  );

  // Show Tests dashboard
  if (showTestsDashboard) {
    return (
      <>
        {qualificationModal}
        <TestsDashboard 
          onBack={() => setShowTestsDashboard(false)} 
          authUser={authUser} 
          onNavigate={navigateToDashboard}
          onMonEspaceClick={(tab) => { setReturnToDashboard('tests'); setShowTestsDashboard(false); if (tab && typeof tab === 'string') setMonEspaceTab(tab); setShowMonEspace(true); }}
          onCalendrierClick={() => { setReturnToDashboard('tests'); setShowTestsDashboard(false); setShowCalendrier(true); }}
          onAddDevis={() => setShowDevisTypeModal(true)}
          onAddLead={() => handleOpenQualificationAppel()}
        />
        {showDevisTypeModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center" onClick={() => setShowDevisTypeModal(false)}>
            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8 max-w-lg w-full mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
              <h2 className="text-xl font-bold text-white mb-2">Nouveau devis</h2>
              <p className="text-sm text-slate-400 mb-6">Selectionnez le type de devis a creer</p>
              <div className="grid gap-3">
                {[
                  { key: 'mpa', label: "MPA - MaPrimeAdapt'", desc: 'Adaptation du logement, accessibilite PMR', color: 'violet', icon: '🏠' },
                  { key: 'pac_iso', label: 'PAC / ISO', desc: 'Pompe a chaleur & Isolation thermique', color: 'orange', icon: '🔥' },
                  { key: 'ts', label: 'TS - Travaux Supplementaires', desc: 'Travaux complementaires hors dispositif', color: 'teal', icon: '🔧' },
                ].map(t => (
                  <button
                    key={t.key}
                    data-testid={`devis-type-${t.key}`}
                    onClick={() => { setShowDevisTypeModal(false); setDevisCreationType(t.key); }}
                    className={`flex items-center gap-4 p-4 rounded-xl border transition-all text-left hover:scale-[1.01] ${
                      t.color === 'violet' ? 'border-violet-500/30 hover:bg-violet-500/10 hover:border-violet-500/60' :
                      t.color === 'orange' ? 'border-orange-500/30 hover:bg-orange-500/10 hover:border-orange-500/60' :
                      'border-teal-500/30 hover:bg-teal-500/10 hover:border-teal-500/60'
                    } bg-slate-800/50`}
                  >
                    <span className="text-2xl">{t.icon}</span>
                    <div>
                      <h3 className={`font-semibold ${
                        t.color === 'violet' ? 'text-violet-400' :
                        t.color === 'orange' ? 'text-orange-400' : 'text-teal-400'
                      }`}>{t.label}</h3>
                      <p className="text-xs text-slate-400">{t.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
              <button onClick={() => setShowDevisTypeModal(false)} className="w-full mt-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">Annuler</button>
            </div>
          </div>
        )}
        {devisCreationType && (
          <DevisCreationForm type={devisCreationType} onClose={() => setDevisCreationType(null)} />
        )}
      </>
    );
  }

  // Show Formations dashboard
  if (showFormations) {
    return (
      <>
        {dashboardNavBar}
        {qualificationModal}
        <FormationsDashboard onBack={() => setShowFormations(false)} authUser={authUser} />
      </>
    );
  }

  // Show Simulateur Aides dashboard
  if (showSimulateurAides) {
    return <>{dashboardNavBar}<SimulateurAidesDashboard onBack={() => setShowSimulateurAides(false)} authUser={authUser} /></>;
  }

  // Show Comité de Direction dashboard
  if (showComiteDirection) {
    return <>{dashboardNavBar}<ComiteDirectionDashboard onBack={() => setShowComiteDirection(false)} authUser={authUser} /></>;
  }

  // Show Calendrier dashboard
  if (showCalendrier) {
    return <>{dashboardNavBar}<CalendrierDashboard 
      onBack={() => {
        setShowCalendrier(false);
        if (returnToDashboard) { navigateToDashboard(returnToDashboard); setReturnToDashboard(null); }
      }} 
      onViewClient={handleViewClientFromCalendar}
      authUser={authUser}
    /></>;
  }

  // Show Mon Espace (espace personnel)
  if (showMonEspace) {
    return <>{dashboardNavBar}<MonEspace 
      onBack={() => {
        setShowMonEspace(false); setMonEspaceTab(null);
        if (returnToDashboard) { navigateToDashboard(returnToDashboard); setReturnToDashboard(null); }
      }} 
      authUser={authUser}
      defaultTab={monEspaceTab}
    /></>;
  }

  // Show Grands Comptes dashboard
  if (showGrandsComptes) {
    return <>{dashboardNavBar}<GrandsComptesDashboard onBack={() => setShowGrandsComptes(false)} /></>;
  }

  // Show Super Batard dashboard (platform admin only)
  if (showSuperBatard) {
    return <>{dashboardNavBar}<SuperBatardDashboard onBack={() => { setShowSuperBatard(false); setSuperBatardInitialSection(null); }} authUser={authUser} initialSection={superBatardInitialSection} /></>;
  }

  // Show Console Admin (tenant admin)
  if (showConsoleAdmin) {
    return <>{dashboardNavBar}<SuperBatardDashboard onBack={() => setShowConsoleAdmin(false)} authUser={authUser} isConsoleAdmin={true} /></>;
  }
  
  // Show Super Batard Compta v2 dashboard
  if (showSuperBatardCompta) {
    return <>{dashboardNavBar}<SuperBatardComptaDashboard onBack={() => setShowSuperBatardCompta(false)} /></>;
  }
  
  // Show Consolidation Comptable v2
  if (showConsolidationV2) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b px-4 py-2">
          <button
            onClick={() => setShowConsolidationV2(false)}
            className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
          >
            ← Retour
          </button>
        </div>
        <ConsolidationComptableV2 />
      </div>
    );
  }
  
  // Show Marketing dashboard
  if (showMarketing) {
    return <>{dashboardNavBar}<MarketingDashboard onBack={() => setShowMarketing(false)} /></>;
  }
  
  // Show Communications Hub
  if (showCommunicationsHub) {
    return <>{dashboardNavBar}<CommunicationsHub onBack={() => setShowCommunicationsHub(false)} /></>;
  }
  
  // Show Administratif dashboard
  if (showAdministratif) {
    return <>{dashboardNavBar}<AdministratifDashboard onBack={() => setShowAdministratif(false)} /></>;
  }

  // Show Aides Complementaires dashboard (SocialAC)
  if (showAidesComp) {
    return <>{dashboardNavBar}<AidesCompDashboard onBack={() => setShowAidesComp(false)} authUser={authUser} /></>;
  }
  
  // Show Chantiers dashboard
  if (showChantiers) {
    const chantiersPole = poles.find(p => p.name === 'Chantiers');
    return (
      <>{dashboardNavBar}<ChantiersDashboard 
        onBack={() => setShowChantiers(false)}
        chantiersPole={chantiersPole}
        onOpenPole={(pole) => {
          setShowChantiers(false);
          setCurrentPole(pole);
        }}
      /></>
    );
  }
  
  // Show Compta/Finances dashboard
  if (showComptaFinances) {
    return (
      <>{dashboardNavBar}<ComptaFinancesDashboard 
        onBack={() => setShowComptaFinances(false)} 
        poles={poles}
        onSelectPole={(pole) => {
          setShowComptaFinances(false);
          setCurrentPole(pole);
        }}
      /></>
    );
  }

  // Show Compta / Finances dashboard
  if (showComptaFiNew) {
    return (
      <>{dashboardNavBar}<ComptaFinancesDashboardNew 
        onBack={() => setShowComptaFiNew(false)} 
        poles={poles}
        onSelectPole={(pole) => {
          setShowComptaFiNew(false);
          setCurrentPole(pole);
        }}
      /></>
    );
  }

  // Show admin page
  if (showAdminPage) {
    return <>{dashboardNavBar}<AdminPage onBack={() => setShowAdminPage(false)} /></>;
  }
  
  // Show factures page
  if (showFacturesPage) {
    return <>{dashboardNavBar}<FacturesPage onBack={() => setShowFacturesPage(false)} /></>;
  }
  
  // Show rapprochement page
  if (showRapprochementPage) {
    return <>{dashboardNavBar}<RapprochementPage onBack={() => setShowRapprochementPage(false)} /></>;
  }
  
  // Show statistiques parcours page
  if (showStatistiquesParcours) {
    return (
      <>{dashboardNavBar}<div className="min-h-screen bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <StatistiquesParcoursPage />
        </div>
      </div></>
    );
  }
  
  // Show analyse dossiers bloqués page
  if (showAnalyseBloques) {
    return (
      <>{dashboardNavBar}<div className="min-h-screen bg-slate-900 p-6">
        <div className="max-w-6xl mx-auto">
          <AnalyseDossiersBloques />
        </div>
      </div></>
    );
  }

  // Show parcours page
  if (showParcoursPage) {
    return (
      <>{dashboardNavBar}<div className="min-h-screen bg-slate-900 p-6">
        <div className="max-w-6xl mx-auto">
          <ParcoursClientParchemin />
        </div>
      </div></>
    );
  }

  // Show Télépro dashboard
  if (showTeleproDashboard) {
    return (
      <>
        {dashboardNavBar}
        <TeleproDashboard 
          onBack={() => setShowTeleproDashboard(false)}
          onClientClick={handleSearchClientClick}
          authUser={authUser}
          dashboardId="telepro"
          onMonEspaceClick={(tab) => { if (tab && typeof tab === 'string') setMonEspaceTab(tab); setShowMonEspace(true); }}
          onQualificationAppelClick={handleOpenQualificationAppel}
          onOpenClientFiche={handleOpenClientFromQualification}
        />
        {qualificationModal}
        {selectedClient && (
          <ClientModal
            client={selectedClient}
            columns={selectedClient.columns}
            dropdowns={dropdowns}
            onClose={() => setSelectedClient(null)}
            onUpdate={() => setSelectedClient(null)}
            onPrendreRdv={handlePrendreRdvFromModal}
            onOpenCalendrier={() => setShowCalendrier(true)}
            authUser={authUser}
            onResumeQualification={handleResumeQualification}
          />
        )}
      </>
    );
  }

  // Show stats page
  if (showStatsPage) {
    return <StatsPage onBack={() => setShowStatsPage(false)} />;
  }

  // Show users management page (admin only)
  if (showUsersPage) {
    return <AdminUsersPage user={authUser} onBack={() => setShowUsersPage(false)} />;
  }

  // Show Salle de Réunion Virtuelle
  if (showSalleReunion) {
    return (
      <>{dashboardNavBar}<ReunionRoom 
        onBack={() => setShowSalleReunion(false)}
      /></>
    );
  }

  // Show Christelle AI Search
  if (showChristelleSearch) {
    return (
      <>
        <ChristelleSearch
          onClose={() => setShowChristelleSearch(false)}
          onClientClick={(rowNumber) => {
            // Charger le client et ouvrir le modal
            handleSearchClientClick(rowNumber);
          }}
        />
        {qualificationModal}
        {selectedClient && (
          <ClientModal
            client={selectedClient}
            columns={selectedClient.columns}
            dropdowns={dropdowns}
            onClose={() => setSelectedClient(null)}
            onUpdate={() => setSelectedClient(null)}
            onPrendreRdv={handlePrendreRdvFromModal}
            onOpenCalendrier={() => setShowCalendrier(true)}
            authUser={authUser}
            onResumeQualification={handleResumeQualification}
          />
        )}
      </>
    );
  }

  // Show search page
  if (showSearchPage) {
    return (
      <>
        <SearchPage
          onBack={() => setShowSearchPage(false)}
          onClientClick={handleSearchClientClick}
        />
        {qualificationModal}
        {selectedClient && (
          <ClientModal
            client={selectedClient}
            columns={selectedClient.columns}
            dropdowns={dropdowns}
            onClose={() => setSelectedClient(null)}
            onUpdate={() => setSelectedClient(null)}
            onPrendreRdv={handlePrendreRdvFromModal}
            onOpenCalendrier={() => setShowCalendrier(true)}
            authUser={authUser}
            onResumeQualification={handleResumeQualification}
          />
        )}
      </>
    );
  }

  // Handle Pole Selection - check for Télépro or Super Admin
  const handlePoleSelect = (pole) => {
    if (pole.name === 'Télépro') {
      setShowTeleproDashboard(true);
    } else if (pole.name === 'Super Admin') {
      setShowSuperBatard(true);
    } else {
      setCurrentPole(pole);
    }
  };

  // Show pole selector if no pole selected
  if (!currentPole && !loading) {
    return (
      <>
        {qualificationModal}
        <PoleSelector 
          poles={poles} 
          onSelect={handlePoleSelect} 
          onSearchClick={() => setShowSearchPage(true)}
          onChristelleClick={() => setShowChristelleSearch(true)}
          onStatsClick={() => setShowStatsPage(true)}
          onAdminClick={() => setShowAdminPage(true)}
          onParcoursClick={() => setShowParcoursPage(true)}
          onFacturesClick={() => setShowFacturesPage(true)}
          onAnalyseBloques={() => setShowAnalyseBloques(true)}
          onStatistiquesParcours={() => setShowStatistiquesParcours(true)}
          onRapprochementClick={() => setShowRapprochementPage(true)}
          onComptaFinancesClick={() => setShowComptaFinances(true)}
          onComptaFiNewClick={() => setShowComptaFiNew(true)}
          onSalleReunionClick={() => setShowSalleReunion(true)}
          onMarketingClick={() => setShowMarketing(true)}
          onSuperBatardClick={() => setShowSuperBatard(true)}
          onConsoleAdminClick={() => setShowConsoleAdmin(true)}
          onSuperBatardComptaClick={() => setShowSuperBatardCompta(true)}
          onConsolidationV2Click={() => setShowConsolidationV2(true)}
          onCommunicationsHubClick={() => setShowCommunicationsHub(true)}
          onAdministratifClick={() => setShowAdministratif(true)}
          onChantiersClick={() => setShowChantiers(true)}
          onAidesCompClick={() => setShowAidesComp(true)}
          onGrandsComptesClick={() => setShowGrandsComptes(true)}
          onCalendrierClick={() => setShowCalendrier(true)}
          onComiteDirectionClick={() => setShowComiteDirection(true)}
          onFormationsClick={() => setShowFormations(true)}
          onSimulateurAidesClick={() => setShowSimulateurAides(true)}
          onTestsDashboardClick={() => setShowTestsDashboard(true)}
          onMonEspaceClick={(tab) => { if (tab && typeof tab === 'string') setMonEspaceTab(tab); setShowMonEspace(true); }}
          onQualificationAppelClick={handleOpenQualificationAppel}
          authUser={authUser}
          onLogout={handleLogout}
          onUsersClick={() => setShowUsersPage(true)}
        />
        {/* Modal client accessible depuis toutes les vues */}
        {selectedClient && (
          <ClientModal
            client={selectedClient}
            columns={selectedClient.columns}
            dropdowns={dropdowns}
            onClose={() => setSelectedClient(null)}
            onUpdate={() => setSelectedClient(null)}
            onPrendreRdv={handlePrendreRdvFromModal}
            onOpenCalendrier={() => setShowCalendrier(true)}
            authUser={authUser}
            onResumeQualification={handleResumeQualification}
          />
        )}
      </>
    );
  }

  if (loading && !currentPole) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const poleConfig = POLE_CONFIG[currentPole?.name] || POLE_CONFIG['Tous'];
  const PoleIcon = poleConfig.icon;
  const poleDisplayName = poleConfig.displayName || currentPole?.name;

  // Get table columns (first 8-10 most important)
  const tableColumns = columns.slice(0, 10);

  return (
    <>
      {qualificationModal}
      {dashboardNavBar}
      <div className="flex bg-slate-900 text-white" style={{ height: "calc(100vh - 42px)" }}>
      {/* Sidebar - Alerts */}
      <aside className={`${sidebarCollapsed ? 'w-16' : 'w-80'} bg-slate-950 border-r border-slate-800 flex flex-col transition-all duration-300`}>
        {/* Header */}
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${poleConfig.bg} rounded-lg flex items-center justify-center`}>
              <PoleIcon className="w-5 h-5 text-white" />
            </div>
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <h1 className="font-bold text-sm truncate">{poleDisplayName}</h1>
                <p className="text-xs text-slate-400">{stats?.total || 0} clients</p>
              </div>
            )}
          </div>
        </div>

        {/* Alerts */}
        {!sidebarCollapsed && (
          <div className="flex-1 overflow-y-auto p-3">
            <div className="flex items-center gap-2 mb-3">
              <Bell className="w-4 h-4 text-yellow-500" />
              <span className="text-xs uppercase tracking-wider text-slate-400 font-medium">Alertes</span>
              {alertFilter && (
                <button 
                  onClick={() => setAlertFilter(null)}
                  className="ml-auto text-xs text-blue-400 hover:text-blue-300"
                >
                  Effacer filtre
                </button>
              )}
            </div>
            <div className="space-y-2">
              {alerts.filter(a => a.count > 0).map((alert, i) => (
                <AlertCard 
                  key={i} 
                  alert={alert} 
                  onClick={() => handleAlertClick(alert)}
                  isActive={alertFilter === alert.status}
                />
              ))}
              {alerts.filter(a => a.count > 0).length === 0 && (
                <p className="text-slate-500 text-sm text-center py-4">Aucune alerte</p>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-3 border-t border-slate-800 space-y-2">
          <button 
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center gap-2 p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg"
          >
            <Menu className="w-4 h-4" />
          </button>
          {!sidebarCollapsed && (
            <button 
              onClick={handleBackToMenu}
              className="w-full flex items-center justify-center gap-2 p-2 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm">Changer de pôle</span>
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-slate-900 border-b border-slate-800 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  data-testid="search-input"
                  type="text"
                  placeholder="Rechercher un client..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {alertFilter && (
                <div className="flex items-center gap-2 bg-blue-500/20 text-blue-400 px-3 py-1.5 rounded-lg">
                  <Filter className="w-4 h-4" />
                  <span className="text-sm truncate max-w-[200px]">{alertFilter}</span>
                  <button onClick={() => setAlertFilter(null)} className="hover:text-white">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              {currentPole?.name === 'R1' && (
                <>
                  <button
                    data-testid="r1-add-lead-btn"
                    onClick={handleOpenQualificationAppel}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-medium transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Ajouter un lead</span>
                  </button>
                  <button
                    data-testid="r1-add-devis-btn"
                    onClick={() => setShowDevisTypeModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg font-medium transition-colors"
                  >
                    <FileEdit className="w-4 h-4" />
                    <span>Ajouter un devis</span>
                  </button>
                </>
              )}
              <TaskNotificationBadge userEmail={authUser?.email} onOpenTasks={() => { setMonEspaceTab('taches_internes'); setShowMonEspace(true); }} />
              {/* Bouton Mon Espace - ouvre la page Mon Espace */}
              <button
                onClick={() => setShowMonEspace(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors"
                data-testid="mon-espace-page-btn"
              >
                <User className="w-4 h-4" />
                Mon Espace
              </button>
              <CalendrierButton authUser={authUser} />
              {/* Bouton Profil avec Avatar (compact) */}
              <MonEspaceButton user={authUser} onLogout={handleLogout} compact={true} />
              <button 
                data-testid="refresh-btn"
                onClick={fetchPoleData}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Actualiser</span>
              </button>
            </div>
          </div>
        </header>

        {/* Clients Table */}
        <div className="flex-1 overflow-auto p-6">
          <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900 sticky top-0 z-10">
                  <tr>
                    {tableColumns.map(col => (
                      <th key={col.index} className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium whitespace-nowrap">
                        {col.title}
                      </th>
                    ))}
                    <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {clients.map(client => (
                    <tr 
                      key={client.row_number} 
                      className="hover:bg-slate-700/50 transition-colors cursor-pointer"
                      onClick={() => handleClientClick(client.row_number)}
                    >
                      {tableColumns.map(col => (
                        <td key={col.index} className="px-4 py-3 text-sm whitespace-nowrap">
                          <span className="truncate block max-w-[200px]" title={client.data[col.title]}>
                            {client.data[col.title] || <span className="text-slate-600">—</span>}
                          </span>
                        </td>
                      ))}
                      <td className="px-4 py-3 text-center flex items-center justify-center gap-1">
                        {client.qualification_step && (
                          <button
                            data-testid={`btn-resume-qual-${client.row_number}`}
                            onClick={e => {
                              e.stopPropagation();
                              handleResumeQualification({
                                row_number: client.row_number,
                                nom: client.data['CLIENT NOM'] || '',
                                prenom: client.data['PRÉNOM'] || '',
                                telephone: client.data['TEL CLIENT'] || '',
                                email: client.data['EMAIL'] || '',
                                code_postal: client.data['CODE POSTAL'] || '',
                                ville: client.data['VILLE'] || '',
                                adresse: client.data['ADRESSE'] || '',
                              });
                            }}
                            className="p-1.5 hover:bg-amber-500/20 rounded-lg group relative"
                            title={`Qualification en cours - Étape ${client.qualification_step}/5`}
                          >
                            <PlayCircle className="w-4 h-4 text-amber-400" />
                          </button>
                        )}
                        <button 
                          onClick={e => { e.stopPropagation(); handleClientClick(client.row_number); }}
                          className="p-2 hover:bg-slate-600 rounded-lg"
                        >
                          <Eye className="w-4 h-4 text-slate-400" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {clients.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                {loading ? (
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                ) : (
                  <p>Aucun client trouvé</p>
                )}
              </div>
            )}
          </div>
          <p className="text-sm text-slate-400 mt-3">{clients.length} clients affichés</p>
        </div>
        
        {/* Bannière Nouveau R1 - uniquement pour le pôle R1 */}
        {currentPole?.name === 'R1' && (
          <BanniereNouveauR1 
            onClientClick={(rowNumber) => {
              console.log('Client R1 clicked:', rowNumber);
              // Ouvrir la fiche client si besoin
            }}
          />
        )}
      </main>

      {/* Client Modal */}
      {selectedClient && (
        <ClientModal
          client={selectedClient}
          columns={selectedClient.columns}
          dropdowns={dropdowns}
          onClose={() => setSelectedClient(null)}
          onUpdate={() => { fetchPoleData(); setSelectedClient(null); }}
          onPrendreRdv={handlePrendreRdvFromModal}
          onOpenCalendrier={() => setShowCalendrier(true)}
          authUser={authUser}
          onResumeQualification={handleResumeQualification}
        />
      )}
      
      {/* Toast notifications */}
      <Toaster position="top-right" richColors />

      {/* Modale choix type de devis */}
      {showDevisTypeModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center" onClick={() => setShowDevisTypeModal(false)}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8 max-w-lg w-full mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold text-white mb-2">Nouveau devis</h2>
            <p className="text-sm text-slate-400 mb-6">Selectionnez le type de devis a creer</p>
            <div className="grid gap-3">
              {[
                { key: 'mpa', label: "MPA - MaPrimeAdapt'", desc: 'Adaptation du logement, accessibilite PMR', color: 'violet', icon: '🏠' },
                { key: 'pac_iso', label: 'PAC / ISO', desc: 'Pompe a chaleur & Isolation thermique', color: 'orange', icon: '🔥' },
                { key: 'ts', label: 'TS - Travaux Supplementaires', desc: 'Travaux complementaires hors dispositif', color: 'teal', icon: '🔧' },
              ].map(t => (
                <button
                  key={t.key}
                  data-testid={`devis-type-${t.key}`}
                  onClick={() => {
                    setShowDevisTypeModal(false);
                    setDevisCreationType(t.key);
                  }}
                  className={`flex items-center gap-4 p-4 rounded-xl border transition-all text-left hover:scale-[1.01] ${
                    t.color === 'violet' ? 'border-violet-500/30 hover:bg-violet-500/10 hover:border-violet-500/60' :
                    t.color === 'orange' ? 'border-orange-500/30 hover:bg-orange-500/10 hover:border-orange-500/60' :
                    'border-teal-500/30 hover:bg-teal-500/10 hover:border-teal-500/60'
                  } bg-slate-800/50`}
                >
                  <span className="text-2xl">{t.icon}</span>
                  <div>
                    <h3 className={`font-semibold ${
                      t.color === 'violet' ? 'text-violet-400' :
                      t.color === 'orange' ? 'text-orange-400' : 'text-teal-400'
                    }`}>{t.label}</h3>
                    <p className="text-xs text-slate-400">{t.desc}</p>
                  </div>
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowDevisTypeModal(false)}
              className="w-full mt-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
      {/* Formulaire creation devis */}
      {devisCreationType && (
        <DevisCreationForm
          type={devisCreationType}
          onClose={() => setDevisCreationType(null)}
        />
      )}
      </div>
    </>
  );
}

export default App;
